"use client";
import React from "react";
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  startOfWeek,
  endOfWeek,
  subWeeks,
  isSameMonth,
  formatISO,
  parseISO,
  startOfMonth,
  endOfMonth,
  format,
} from "date-fns";
import * as XLSX from "xlsx";
import Select from "react-select";
import AlertDialog from "./AlertDialog";
import UnionDuesExportModal from "./UnionDuesExportModal";
import UnionDuesHistoryModal from "./UnionDuesHistoryModal";

const ACCENT = "#f97316";
const API = "https://erp-xe9u.onrender.com/api";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#161a20",
    borderColor: state.isFocused ? ACCENT : "#2a2f37",
    color: "#e7e9ec",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: ACCENT },
    minHeight: "34px",
    height: "34px",
    fontSize: "12.5px",
    borderRadius: "6px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e7e9ec",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e7e9ec",
    fontSize: "12.5px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#6b7280",
    fontSize: "12.5px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#22262e" : "#161a20",
    color: state.isSelected ? ACCENT : "#e7e9ec",
    "&:hover": { backgroundColor: "#22262e" },
    fontSize: "12.5px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#161a20",
    borderRadius: "8px",
    border: "1px solid #2a2f37",
    boxShadow: "0 12px 32px -8px rgba(0,0,0,0.6)",
    zIndex: 60,
  }),
  valueContainer: (provided) => ({ ...provided, padding: "0 8px" }),
  indicatorsContainer: (provided) => ({ ...provided, padding: "0 4px" }),
  dropdownIndicator: (provided) => ({ ...provided, padding: "2px" }),
};

const PAY_TYPE_META = {
  hourly: { label: "Hourly", tint: "#3b82f6" },
  pieceworker: { label: "Piecework", tint: ACCENT },
  salary: { label: "Salary", tint: "#a78bfa" },
  subcontractor: { label: "Subcontractor", tint: "#34d399" },
  expense: { label: "Expense", tint: "#f472b6" },
  uniondues: { label: "Union Dues", tint: "#ef4444" },
};

const money = (n) =>
  (Number(n) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const cls = (...c) => c.filter(Boolean).join(" ");

// Formats a timestamp as a local clock time (e.g. "7:32 AM"). Returns
// "—" for missing/invalid values so picker cells never show "Invalid Date".
const clockTime = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

// Sum of break durations (minutes) on a timesheet item — mirrors the
// backend's computeTimesheetHours(), which sums breaks[].duration in
// minutes and subtracts that from worked time. available/timesheets
// doesn't send a pre-summed breakMinutes field, so we total it here from
// the same `breaks` array the backend uses.
const breakMinutesTotal = (item) =>
  (item.breaks || []).reduce(
    (sum, b) => sum + (parseInt(b.duration, 10) || 0),
    0,
  );

const formatBreakDuration = (minutes) => {
  if (!minutes) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

// Preview-only calc mirroring the backend's rounding rule exactly:
// hours mode rounds UP to the nearest quarter-hour.
const previewDuesDeduction = (item, method) => {
  const duesAmount = item.configuredDuesAmount || 0;
  if (method === "dollars") {
    return { amountDeducted: duesAmount, hoursDeducted: 0, blocked: false };
  }
  const rate = item.payRate || 0;
  if (rate <= 0) {
    return { amountDeducted: 0, hoursDeducted: 0, blocked: true };
  }
  const rawHours = duesAmount / rate;
  const hoursDeducted = Math.ceil(rawHours * 4) / 4;
  const amountDeducted = Number((hoursDeducted * rate).toFixed(2));
  return { amountDeducted, hoursDeducted, blocked: false };
};

// A uniondues row can't be selected if it's already been charged this
// month, or if there's no dues amount configured for the worker's union.
const isDuesRowLocked = (item) =>
  !!item.alreadyCharged || !(item.configuredDuesAmount > 0);

const StatusPill = ({ status }) => {
  const map = {
    processed: { bg: "rgba(52,211,153,0.12)", fg: "#34d399", label: "Locked" },
    exported: { bg: "rgba(59,130,246,0.12)", fg: "#60a5fa", label: "Exported" },
    pending: { bg: "rgba(250,204,21,0.12)", fg: "#facc15", label: "Draft" },
  };
  const s = map[status] || map.pending;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium tracking-wide"
      style={{ backgroundColor: s.bg, color: s.fg }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: s.fg }}
      />
      {s.label}
    </span>
  );
};

const PayTypeTag = ({ payType }) => {
  const meta = PAY_TYPE_META[payType?.toLowerCase()] || {
    label: payType,
    tint: "#9ca3af",
  };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border"
      style={{
        color: meta.tint,
        borderColor: `${meta.tint}40`,
        backgroundColor: `${meta.tint}14`,
      }}
    >
      {meta.label}
    </span>
  );
};

const LockIcon = ({ locked }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    {locked ? (
      <path
        d="M6 10V8a6 6 0 1112 0v2M5 10h14a1 1 0 011 1v9a2 2 0 01-2 2H6a2 2 0 01-2-2v-9a1 1 0 011-1z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : (
      <path
        d="M6 10V8a6 6 0 0110.9-3.4M5 10h14a1 1 0 011 1v9a2 2 0 01-2 2H6a2 2 0 01-2-2v-9a1 1 0 011-1z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </svg>
);

// Header cell for a picker-table value column (Hours, Subtotal, Tax,
// Total, Configured, Deduction, ...) that has no filter — just a click-
// to-sort toggle. `active`/`dir` come from the pickerSort state; `onSort`
// is togglePickerSort(sortKey).
const SortableTh = ({
  label,
  sortKey,
  align = "right",
  active,
  dir,
  onSort,
}) => (
  <th
    className={cls(
      "px-3 py-2 text-[11px] uppercase tracking-wider select-none",
      align === "right" ? "text-right" : "text-left",
    )}
  >
    <button
      onClick={(e) => {
        e.stopPropagation();
        onSort(sortKey);
      }}
      title="Sort"
      className={cls(
        "inline-flex items-center gap-1 hover:text-orange-300 transition-colors",
        active ? "text-orange-400" : "text-gray-500",
      )}
    >
      {label}
      {active && (
        <span className="text-orange-400">{dir === "asc" ? "↑" : "↓"}</span>
      )}
    </button>
  </th>
);

const FilterableTh = ({
  col,
  align = "left",
  activeSort,
  sortDir,
  onSort,
  isOpen,
  onToggleOpen, // (e, key) => void
  isFilterActive,
}) => (
  <th
    className={cls(
      "px-3 py-2 text-[11px] uppercase tracking-wider select-none",
      align === "right" ? "text-right" : "text-left",
    )}
  >
    <span className="inline-flex items-center gap-1">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSort(col.key);
        }}
        className={cls(
          "inline-flex items-center gap-1 hover:text-orange-300 transition-colors",
          isFilterActive ? "text-orange-400" : "text-gray-500",
        )}
        title="Sort"
      >
        {col.label}
        {isFilterActive && (
          <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
        )}
        {activeSort && (
          <span className="text-orange-400">
            {sortDir === "asc" ? "↑" : "↓"}
          </span>
        )}
      </button>
      <button
        data-report-filter-trigger
        onClick={(e) => {
          e.stopPropagation();
          onToggleOpen(e, col.key);
        }}
        title="Filter"
        className={cls(
          "inline-flex items-center justify-center w-6 h-6 rounded-md border transition-all",
          isOpen || isFilterActive
            ? "bg-orange-500 border-orange-400 text-white"
            : "bg-[#1c2027] border-[#2a2f37] text-gray-400 hover:text-orange-300 hover:border-orange-500/50",
        )}
      >
        <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor">
          <path d="M3 4a1 1 0 011-1h12a1 1 0 01.8 1.6l-4.8 6.4V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-4L3.2 4.6A1 1 0 013 4z" />
        </svg>
      </button>
    </span>
  </th>
);
// ── Picker column config ──────────────────────────────────────────────────
// Defines which columns are filterable per picker kind, their type
// (text = contains-match input; choice = Excel-style checkbox list of
// distinct values), and how to read the value off an item. Salary only
// gets a Worker filter — it's just a worker selection, no other
// filterable columns. Hoisted outside the component since it's static.
// ── Picker column config ──────────────────────────────────────────────────

const PICKER_COLUMNS = {
  timesheet: [
    { key: "worker", label: "Worker", type: "choice", get: (i) => i.worker },
    {
      key: "date",
      label: "Date",
      type: "choice",
      isDate: true,
      get: (i) => (i.start ? new Date(i.start).toLocaleDateString() : "—"),
      rawGet: (i) => i.start,
    },
    {
      key: "project",
      label: "Project",
      type: "choice",
      get: (i) => i.project || "—",
    },
    {
      key: "woNumber",
      label: "Work Order",
      type: "choice",
      get: (i) => i.woNumber || "—",
    },
  ],
  uniondues: [
    { key: "worker", label: "Worker", type: "choice", get: (i) => i.worker },
    {
      key: "union",
      label: "Union",
      type: "choice",
      get: (i) => i.union || "—",
    },
    {
      key: "status",
      label: "Status",
      type: "choice",
      get: (i) =>
        i.alreadyCharged
          ? "Charged"
          : i.configuredDuesAmount
            ? "Eligible"
            : "No dues set",
    },
  ],
  piecework: [
    { key: "worker", label: "Worker", type: "choice", get: (i) => i.worker },
    {
      key: "logNumber",
      label: "Log #",
      type: "choice",
      get: (i) => i.logNumber || "—",
    },
    {
      key: "project",
      label: "Project",
      type: "choice",
      get: (i) => i.project || "—",
    },
    {
      key: "woNumber",
      label: "Work Order",
      type: "choice",
      get: (i) => i.woNumber || "—",
    },
    {
      key: "workCompletedDate",
      label: "Work Completed",
      type: "choice",
      isDate: true,
      get: (i) =>
        i.workCompletedDate
          ? new Date(i.workCompletedDate).toLocaleDateString()
          : "—",
      rawGet: (i) => i.workCompletedDate,
    },
    {
      key: "approvedAt",
      label: "Approved",
      type: "choice",
      isDate: true,
      get: (i) =>
        i.approvedAt ? new Date(i.approvedAt).toLocaleDateString() : "—",
      rawGet: (i) => i.approvedAt,
    },
  ],
  bill: [
    { key: "worker", label: "Worker", type: "choice", get: (i) => i.worker },
    {
      key: "invoiceNumber",
      label: "Invoice #",
      type: "choice",
      get: (i) => i.invoiceNumber,
    },
    {
      key: "project",
      label: "Project",
      type: "choice",
      get: (i) => i.project || "—",
    },
  ],
  agreement: [
    { key: "worker", label: "Worker", type: "choice", get: (i) => i.worker },
    {
      key: "invoiceNumber",
      label: "Agreement #",
      type: "choice",
      get: (i) => i.invoiceNumber,
    },
    {
      key: "project",
      label: "Project",
      type: "choice",
      get: (i) => i.project || "—",
    },
  ],
  salary: [
    { key: "worker", label: "Worker", type: "choice", get: (i) => i.worker },
  ],
  expense: [
    { key: "worker", label: "Employee", type: "choice", get: (i) => i.worker },
    {
      key: "invoiceNumber",
      label: "Invoice #",
      type: "choice",
      get: (i) => i.invoiceNumber,
    },
    {
      key: "project",
      label: "Project",
      type: "choice",
      get: (i) => i.project || "—",
    },
    {
      key: "moNumber",
      label: "MO #",
      type: "choice",
      get: (i) => (i.moNumber != null ? String(i.moNumber) : "—"),
    },
  ],
};

const EXPORT_OPTIONS = [
  { value: "", label: "Select an option" },
  { value: "hourly", label: "CSV (Hourly + Salary — Wagepoint)" },
  { value: "csv", label: "CSV (Earnings — Piecework + Subcontractor)" },
  { value: "ripplingHourly", label: "CSV (Hourly + Piecework — Rippling)" },
  { value: "ripplingSalary", label: "CSV (Salary — Rippling)" },
  {
    value: "hourlyWorkers",
    label: "Excel (Hourly Site Workers — Hours + Rate)",
  },
  { value: "unionDues", label: "Union Dues (Excel)" },
];

// template 4.csv — Hourly + Piecework
const RIPPLING_HOURLY_HEADERS = [
  "Rippling Emp No",
  "Employee Name",
  "Workweek",
  "Shift ID",
  "Hourly Hours",
  "Hourly Rate",
  "Hourly Amount",
  "Contractor Invoices",
  "Contractor Payments",
  "Hourly overtime (1.5x base) Hours",
  "Hourly overtime (1.5x base) Rate",
  "Hourly overtime (1.5x base) Amount",
  "Hourly double overtime (2x base) Hours",
  "Hourly double overtime (2x base) Rate",
  "Hourly double overtime (2x base) Amount",
  "Expense Reimbursement",
  "GST",
  "VAT",
  "Sales Tax",
  "HST",
  "PST",
  "QST",
  "Other taxes",
  "Hourly overtime (1x base) Hours",
  "Hourly overtime (1x base) Rate",
  "Hourly overtime (1x base) Amount",
  "Vacation Pay - Non Accrual",
  "Union Dues ER Employer Contribution",
  "Union Dues Employee Employee Contribution",
  "Union Dues ER ER Taxable Contribution Value",
  "Paystub Note",
];

// template 5.csv — Salary
const RIPPLING_SALARY_HEADERS = [
  "Rippling Emp No",
  "Employee Name",
  "Workweek",
  "Shift ID",
  "Hourly Hours",
  "Hourly Rate",
  "Hourly Amount",
  "Salary Hours",
  "Salary Amount",
  "Hourly overtime (1.5x base) Hours",
  "Hourly overtime (1.5x base) Rate",
  "Hourly overtime (1.5x base) Amount",
  "Hourly double overtime (2x base) Hours",
  "Hourly double overtime (2x base) Rate",
  "Hourly double overtime (2x base) Amount",
  "Expense Reimbursement",
  "Holiday Pay Hours",
  "Holiday Pay Amount",
  "Hourly overtime (1x base) Hours",
  "Hourly overtime (1x base) Rate",
  "Hourly overtime (1x base) Amount",
  "Vacation Pay - Non Accrual",
  "Union Dues ER Employer Contribution",
  "Union Dues Employee Employee Contribution",
  "Union Dues ER ER Taxable Contribution Value",
  "Paystub Note",
];

// Pay types whose subtotal goes in "Contractor Payments".
// Remove "subcontractor" if those bills shouldn't be in this file.
const CONTRACTOR_PAY_TYPES = ["pieceworker", "subcontractor"];

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const buildRipplingCSV = (headers, rowObjects) =>
  [
    headers.map(csvCell).join(","),
    ...rowObjects.map((row) =>
      headers.map((h) => csvCell(row[h] ?? "")).join(","),
    ),
  ].join("\n");

const downloadCSVFile = (content, filename) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Workers with paymentType "hourly" are paid via the separate upload.
// Everything for them (hourly, expenses, union dues, anything else) is in
// that file and excluded from every other export.
const isHourlyWorkerRow = (r) => !!r.isHourlyContractType;

const PAYROLL_GROUP = {
  hourlySite: "Hourly Site Workers",
  main: "Contractor Site Worker",
};
const payrollGroupOf = (r) =>
  isHourlyWorkerRow(r) ? PAYROLL_GROUP.hourlySite : PAYROLL_GROUP.main;

export default function PayrollView() {
  const token = () => localStorage.getItem("token");
  const headers = () => ({ Authorization: `Bearer ${token()}` });
  const REPORT_COLUMNS = [
    {
      key: "employeeNumber",
      label: "Employee #",
      type: "choice",
      get: (r) => r.employeeNumber || "N/A",
    },
    {
      key: "displayName",
      label: "Name",
      type: "choice",
      get: (r) => r.displayName || "Unknown",
    },
    {
      key: "payType",
      label: "Pay Type",
      type: "choice",
      get: (r) =>
        PAY_TYPE_META[r.payType?.toLowerCase()]?.label || r.payType || "—",
    },
    {
      key: "payrollGroup",
      label: "Group",
      type: "choice",
      get: (r) => payrollGroupOf(r),
    },
    {
      key: "paymentMethod",
      label: "Payment",
      type: "choice",
      get: (r) => (r.paymentMethod === "cheque" ? "Cheque" : "Direct Deposit"),
    },
  ];
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [payrollReport, setPayrollReport] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onConfirm: () => {},
  });
  const [reportColumnFilters, setReportColumnFilters] = useState({});
  const [chequeModal, setChequeModal] = useState({
    open: false,
    record: null,
    value: "",
  });
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [openReportFilterColumn, setOpenReportFilterColumn] = useState(null);
  const [reportSort, setReportSort] = useState({ key: null, dir: "asc" });
  const [weekPeriods, setWeekPeriods] = useState([]);
  const [selectedMonday, setSelectedMonday] = useState("");
  const [period, setPeriod] = useState(null);
  const [duesMethod, setDuesMethod] = useState("dollars"); // "dollars" | "hours"
  const [weekOffset, setWeekOffset] = useState(0);
  const [filterDate, setFilterDate] = useState("");
  const [viewMode, setViewMode] = useState("periods");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportStep, setExportStep] = useState(1);
  const [reimburseToOverrides, setReimburseToOverrides] = useState({});
  const [reimbursableUsers, setReimbursableUsers] = useState([]);
  const [exportType, setExportType] = useState("");
  const [exportStartWeek, setExportStartWeek] = useState("");
  const [exportEndWeek, setExportEndWeek] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(
    format(new Date(), "yyyy-MM"),
  );
  const [unions, setUnions] = useState([]);
  const [selectedUnion, setSelectedUnion] = useState("all");
  const [duesExportOpen, setDuesExportOpen] = useState(false);
  const [duesHistoryOpen, setDuesHistoryOpen] = useState(false);

  const [missingIdConfirm, setMissingIdConfirm] = useState({
    open: false,
    workers: [], // [{ displayName }]
    onProceed: null, // () => void
  });
  const [reportFilterDropdownPos, setReportFilterDropdownPos] = useState(null);
  const [filterDropdownPos, setFilterDropdownPos] = useState(null); // { top, left } | null
  const [selectedPeriods, setSelectedPeriods] = useState(new Set());
  const [expandedRow, setExpandedRow] = useState(null);
  const [breakdownData, setBreakdownData] = useState({});
  const [breakdownLoading, setBreakdownLoading] = useState({});

  // Lock state for the currently-viewed single period.
  const [periodLocked, setPeriodLocked] = useState(false);

  // Status per week for the periods table (locked/draft + line count).
  const [periodStatusByWeek, setPeriodStatusByWeek] = useState({});

  // "Add items" picker modal
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKind, setPickerKind] = useState(null); // 'timesheet' | 'piecework' | 'bill' | 'salary'
  const [pickerItems, setPickerItems] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerLockedToPeriod, setPickerLockedToPeriod] = useState(false);
  const [pickerSelected, setPickerSelected] = useState(new Set());
  const [exportRangeMode, setExportRangeMode] = useState("week"); // 'week' | '2weeks' | 'month' | 'custom'
  const [exportAnchorWeek, setExportAnchorWeek] = useState("");
  const [pickerSubmitting, setPickerSubmitting] = useState(false);
  // Salary picker allows per-user hour overrides before confirming
  const [salaryHourOverrides, setSalaryHourOverrides] = useState({});

  // Excel-style per-column filters for the picker table. Keyed by field
  // name. Text columns (worker, logNumber, invoiceNumber) store a plain
  // string (contains-match). Choice columns (woNumber, date-bucketed)
  // store a Set of selected distinct values — empty/absent Set means "no
  // filter applied" for that column.
  const [pickerColumnFilters, setPickerColumnFilters] = useState({});
  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  // Sort applied to the picker table. `key` matches a PICKER_COLUMNS key
  // OR one of the extra non-filterable value columns (hours/subtotal/tax/
  // total/configuredDuesAmount/deduction) — see pickerSortValue below.
  const [pickerSort, setPickerSort] = useState({ key: null, dir: "asc" });

  // "Add to period" dropdown target — used both from within a period view
  // and (optionally) from a global add flow. Defaults to current week.
  const [unlockedPeriods, setUnlockedPeriods] = useState([]);
  const [addTargetWeek, setAddTargetWeek] = useState("");

  const [lockConfirm, setLockConfirm] = useState(false);
  const [unlockConfirm, setUnlockConfirm] = useState(false);

  useEffect(() => {
    const fetchCompanyProfile = async () => {
      try {
        const response = await axios.get(`${API}/company-profile`, {
          headers: headers(),
        });
        setUnions([{ title: "All Unions" }, ...(response.data.unions || [])]);
      } catch (err) {
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message:
            "Failed to load company profile: " +
            (err.response?.data?.message || err.message),
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
      }
    };
    fetchCompanyProfile();
  }, []);

  useEffect(() => {
    if (!openReportFilterColumn) return;
    const handleClick = (e) => {
      if (
        e.target.closest?.("[data-report-filter-dropdown]") ||
        e.target.closest?.("[data-report-filter-trigger]")
      )
        return;
      setOpenReportFilterColumn(null);
      setReportFilterDropdownPos(null);
    };
    const handleScroll = (e) => {
      if (e.target?.closest?.("[data-report-filter-dropdown]")) return;
      setOpenReportFilterColumn(null);
      setReportFilterDropdownPos(null);
    };
    document.addEventListener("mousedown", handleClick);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openReportFilterColumn]);

  const reportColumnOptions = (colKey) => {
    const col = REPORT_COLUMNS.find((c) => c.key === colKey);
    if (!col) return [];

    const norm = (v) => (v == null || v === "" ? "—" : String(v));

    // Rows that pass every OTHER column's filter (this column's own filter
    // is ignored so you can still widen your selection).
    const rows = payrollReport.filter((record) => {
      for (const other of REPORT_COLUMNS) {
        if (other.key === colKey) continue;
        const fv = reportColumnFilters[other.key];
        if (!(fv instanceof Set) || fv.size === 0) continue;
        if (!fv.has(norm(other.get(record)))) return false;
      }
      return true;
    });

    const values = new Set(rows.map((r) => norm(col.get(r))));

    // Keep already-selected values listed so they can be unchecked.
    const current = reportColumnFilters[colKey];
    if (current instanceof Set) current.forEach((v) => values.add(v));

    return Array.from(values).sort((a, b) => a.localeCompare(b));
  };

  const setReportTextFilter = (colKey, value) => {
    setReportColumnFilters((prev) => ({ ...prev, [colKey]: value }));
  };

  const toggleReportChoiceFilterValue = (colKey, value) => {
    setReportColumnFilters((prev) => {
      const current =
        prev[colKey] instanceof Set ? new Set(prev[colKey]) : new Set();
      if (current.has(value)) current.delete(value);
      else current.add(value);
      return { ...prev, [colKey]: current };
    });
  };

  const clearReportColumnFilter = (colKey) => {
    setReportColumnFilters((prev) => {
      const next = { ...prev };
      delete next[colKey];
      return next;
    });
  };

  const clearAllReportFilters = () => setReportColumnFilters({});

  const isReportColumnFilterActive = (colKey) => {
    const v = reportColumnFilters[colKey];
    if (v == null) return false;
    if (v instanceof Set) return v.size > 0;
    return String(v).trim().length > 0;
  };

  const filteredPayrollReport = useMemo(() => {
    return payrollReport.filter((record) => {
      for (const col of REPORT_COLUMNS) {
        const filterVal = reportColumnFilters[col.key];
        if (filterVal == null) continue;
        if (!(filterVal instanceof Set) || filterVal.size === 0) continue;
        const rawVal = col.get(record);
        const itemVal = rawVal == null || rawVal === "" ? "—" : String(rawVal);
        if (!filterVal.has(itemVal)) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payrollReport, reportColumnFilters]);

  const reportSortValue = (record, key) => {
    const col = REPORT_COLUMNS.find((c) => c.key === key);
    if (col) {
      const v = col.get(record);
      return v == null || v === "" ? "" : v;
    }
    switch (key) {
      case "hoursWorked":
        return record.hoursWorked ?? 0;
      case "payRate":
        return record.payRate ?? 0;
      case "amount":
        return record.amount ?? 0;
      case "tax":
        return record.tax ?? 0;
      case "totalPay":
        return record.totalPay ?? 0;
      default:
        return "";
    }
  };

  const sortedPayrollReport = useMemo(() => {
    if (!reportSort.key) return filteredPayrollReport;
    const { key, dir } = reportSort;
    const factor = dir === "asc" ? 1 : -1;
    return [...filteredPayrollReport].sort((a, b) => {
      const va = reportSortValue(a, key);
      const vb = reportSortValue(b, key);
      if (typeof va === "number" && typeof vb === "number") {
        return (va - vb) * factor;
      }
      return (
        String(va).localeCompare(String(vb), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * factor
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPayrollReport, reportSort]);

  const toggleReportSort = (key) => {
    setReportSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: "asc" };
    });
  };

  // ── Shared helper: check for missing employee IDs, prompt if needed ─────
  // `records` = array of { displayName, employeeNumber }
  // `onProceed` = callback to run once confirmed (or immediately if none missing)
  const withEmployeeIdCheck = (records, onProceed) => {
    const missing = records.filter(
      (r) => !r.employeeNumber || String(r.employeeNumber).trim() === "",
    );
    if (missing.length === 0) {
      onProceed();
      return;
    }
    setMissingIdConfirm({
      open: true,
      workers: missing.map((r) => ({
        displayName: r.displayName || "Unknown",
      })),
      onProceed,
    });
  };

  const confirmMissingIdExport = () => {
    const { onProceed } = missingIdConfirm;
    setMissingIdConfirm({ open: false, workers: [], onProceed: null });
    if (onProceed) onProceed();
  };

  const cancelMissingIdExport = () =>
    setMissingIdConfirm({ open: false, workers: [], onProceed: null });

  useEffect(() => {
    const generateWeeks = () => {
      const dates = [];
      const today = new Date();
      const baseDate = filterDate ? new Date(filterDate) : today;
      const baseMonday = startOfWeek(baseDate, { weekStartsOn: 1 });

      for (let i = 0; i < 12 + weekOffset; i++) {
        const monday = subWeeks(baseMonday, i);
        const sunday = endOfWeek(monday, { weekStartsOn: 1 });
        const mondayISO = formatISO(monday, { representation: "date" });
        const sundayISO = formatISO(sunday, { representation: "date" });

        dates.push({
          value: mondayISO,
          start: mondayISO,
          end: sundayISO,
          isCurrentMonth: isSameMonth(monday, today),
          isRecent: i <= 2 && !filterDate,
        });
      }

      setWeekPeriods(dates);
      setSelectedMonday(dates[0].value);
      setExportStartWeek(dates[0].value);
      setExportEndWeek(dates[0].value);
    };
    generateWeeks();
  }, [weekOffset, filterDate]);

  const fetchPeriodStatusForWeeks = async (mondayISOs) => {
    try {
      const results = await Promise.all(
        mondayISOs.map((w) =>
          axios
            .get(`${API}/payroll/period-status`, {
              headers: headers(),
              params: { weekStart: w },
            })
            .then((r) => [w, r.data])
            .catch(() => [w, null]),
        ),
      );
      setPeriodStatusByWeek((prev) => {
        const next = { ...prev };
        results.forEach(([w, data]) => {
          if (data) next[w] = data;
        });
        return next;
      });
    } catch {
      // non-critical
    }
  };

  useEffect(() => {
    if (weekPeriods.length > 0) {
      fetchPeriodStatusForWeeks(weekPeriods.map((w) => w.value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekPeriods]);

  const fetchUnlockedPeriods = async () => {
    try {
      const res = await axios.get(`${API}/payroll/unlocked-periods`, {
        headers: headers(),
        params: { weeksBack: 12 },
      });
      const periods = res.data.periods || [];
      setUnlockedPeriods(periods);
      const current = periods.find((p) => p.isCurrent);
      setAddTargetWeek(
        (prev) => prev || current?.weekStart || periods[0]?.weekStart || "",
      );
    } catch (err) {
      console.error("Failed to load unlocked periods:", err);
    }
  };

  useEffect(() => {
    fetchUnlockedPeriods();
  }, []);

  // Close an open picker column-filter dropdown on outside click.
  useEffect(() => {
    if (!openFilterColumn) return;
    const handleClick = (e) => {
      if (!e.target.closest?.("[data-picker-filter-dropdown]")) {
        setOpenFilterColumn(null);
        setFilterDropdownPos(null);
      }
    };
    // Only close on scroll that happens OUTSIDE the open dropdown itself.
    // Previously this fired on every scroll in capture phase — including
    // scrolling the checkbox list inside the dropdown — which closed the
    // panel the instant you tried to scroll through filter options. Now
    // we ignore scroll events whose target is inside the dropdown (or is
    // the dropdown's own scrollable list), and only close when the
    // *underlying page/table* scrolls (e.g. the picker's row list, or the
    // page behind the modal), since the dropdown is `position: fixed` and
    // would otherwise drift away from its trigger button.
    const handleScroll = (e) => {
      if (e.target?.closest?.("[data-picker-filter-dropdown]")) return;
      setOpenFilterColumn(null);
      setFilterDropdownPos(null);
    };
    document.addEventListener("mousedown", handleClick);
    window.addEventListener("scroll", handleScroll, true); // capture: still needed to catch the picker table's internal scroll container
    return () => {
      document.removeEventListener("mousedown", handleClick);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openFilterColumn]);

  useEffect(() => {
    const fetchPayrollHistory = async () => {
      setLoading(true);
      setAlert((prev) => ({ ...prev, open: false }));
      try {
        const response = await axios.get(`${API}/payroll/history`, {
          headers: headers(),
        });
        setPayrollHistory(response.data.payrollHistory);
      } catch (err) {
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message:
            "Failed to load payroll history: " +
            (err.response?.data?.message || err.message),
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
      } finally {
        setLoading(false);
      }
    };
    fetchPayrollHistory();
  }, []);

  useEffect(() => {
    if (viewMode === "report") {
      setSelectedPeriods(new Set());
      setExpandedRow(null);
      setBreakdownData({});
    }
  }, [viewMode]);

  const calculatePeriodDates = (mondayISO) => {
    if (!mondayISO) return null;
    const monday = startOfWeek(parseISO(mondayISO), { weekStartsOn: 1 });
    const sunday = endOfWeek(monday, { weekStartsOn: 1 });
    return {
      start: formatISO(monday, { representation: "date" }),
      end: formatISO(sunday, { representation: "date" }),
    };
  };

  const resolveExportRange = (mode, anchorMondayISO) => {
    if (!anchorMondayISO) return null;
    const anchorWeek = calculatePeriodDates(anchorMondayISO);
    if (!anchorWeek) return null;

    if (mode === "week") {
      return { start: anchorMondayISO, end: anchorWeek.end };
    }
    if (mode === "2weeks") {
      const startMonday = subWeeks(parseISO(anchorMondayISO), 1);
      return {
        start: formatISO(startMonday, { representation: "date" }),
        end: anchorWeek.end,
      };
    }
    if (mode === "month") {
      const anchorDate = parseISO(anchorMondayISO);
      const monthStart = startOfMonth(anchorDate);
      const monthEnd = endOfMonth(anchorDate);
      let firstMonday = startOfWeek(monthStart, { weekStartsOn: 1 });
      if (firstMonday < monthStart) firstMonday = subWeeks(firstMonday, -1);
      let lastMonday = startOfWeek(monthEnd, { weekStartsOn: 1 });
      if (lastMonday > monthEnd) lastMonday = subWeeks(lastMonday, 1);
      const lastWeek = calculatePeriodDates(
        formatISO(lastMonday, { representation: "date" }),
      );
      return {
        start: formatISO(firstMonday, { representation: "date" }),
        end: lastWeek.end,
      };
    }
    return null; // custom
  };

  useEffect(() => {
    if (exportRangeMode === "custom") return;
    if (!exportAnchorWeek) return;
    const range = resolveExportRange(exportRangeMode, exportAnchorWeek);
    if (range) {
      setExportStartWeek(range.start);
      setExportEndWeek(range.end);
    }
  }, [exportRangeMode, exportAnchorWeek]);

  const fetchPayrollReport = async (mondayISO) => {
    if (!mondayISO) return;
    setLoading(true);
    setAlert((prev) => ({ ...prev, open: false }));
    try {
      const period = calculatePeriodDates(mondayISO);
      if (!period) throw new Error("Invalid period selected");
      const { start } = period;
      const response = await axios.get(`${API}/payroll`, {
        headers: headers(),
        params: { weekStart: start },
      });
      setPayrollReport(response.data.payrollReport || []);
      setLineItems(response.data.lineItems || []);
      setPeriodLocked(response.data.locked || false);
      setPeriod({ ...period, count: undefined });
      setViewMode("report");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to load payroll report: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      setPayrollReport([]);
      setLineItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPeriod = (mondayISO) => {
    setSelectedMonday(mondayISO);
    fetchPayrollReport(mondayISO);
  };

  const handleLoadMore = () => setWeekOffset((prev) => prev + 12);

  const handleBack = () => {
    setViewMode("periods");
    setPayrollReport([]);
    setLineItems([]);
    setPeriod(null);
    setSelectedPeriods(new Set());
    setExpandedRow(null);
    setBreakdownData({});
    fetchPeriodStatusForWeeks(weekPeriods.map((w) => w.value));
    fetchUnlockedPeriods();
  };

  const formatDate = (isoDate) => {
    const [year, month, day] = isoDate.split("-");
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  const allWeeksSelected =
    weekPeriods.length > 0 &&
    weekPeriods.every((w) => selectedPeriods.has(w.value));

  const toggleAllWeeks = () =>
    setSelectedPeriods(
      allWeeksSelected ? new Set() : new Set(weekPeriods.map((w) => w.value)),
    );

  const togglePeriodSelection = (mondayISO) => {
    setSelectedPeriods((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(mondayISO)) newSet.delete(mondayISO);
      else newSet.add(mondayISO);
      return newSet;
    });
  };

  const handleViewSelectedPeriods = async () => {
    if (selectedPeriods.size === 0) return;
    setLoading(true);
    try {
      const periodsStr = Array.from(selectedPeriods).join(",");
      const res = await axios.get(`${API}/payroll/multi-period`, {
        headers: headers(),
        params: { periods: periodsStr },
      });
      setPayrollReport(res.data.payrollReport);
      setLineItems([]);

      const sortedPeriods = Array.from(selectedPeriods).sort();
      const firstMonday = sortedPeriods[0];
      const lastMonday = sortedPeriods[sortedPeriods.length - 1];
      const lastMondayDate = new Date(lastMonday);
      const lastSunday = endOfWeek(lastMondayDate, { weekStartsOn: 1 });
      const lastSundayISO = formatISO(lastSunday, { representation: "date" });

      setPeriod({
        start: firstMonday,
        end: lastSundayISO,
        count: selectedPeriods.size,
      });
      setViewMode("report");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to load selected periods: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert((p) => ({ ...p, open: false })),
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleRowExpansion = async (record) => {
    const userId = record._id || null;
    const payType = record.payType?.toLowerCase() || "";
    const rowKey = `${userId}_${payType}`;

    if (!userId) {
      setAlert({
        open: true,
        type: "error",
        title: "Cannot load breakdown",
        message: "Missing user ID for this record",
        onConfirm: () => setAlert((p) => ({ ...p, open: false })),
      });
      return;
    }

    if (expandedRow === rowKey) {
      setExpandedRow(null);
      return;
    }

    setExpandedRow(rowKey);
    if (breakdownData[rowKey]) return;

    setBreakdownLoading((prev) => ({ ...prev, [rowKey]: true }));

    try {
      // Use the plain YYYY-MM-DD Monday string directly — do NOT round-trip
      // through `new Date(...).toISOString()`. That conversion reads a
      // date-only string as UTC midnight, and date-fns's startOfWeek/
      // startOfDay on the backend operate in server-local time, so in any
      // timezone behind UTC the same calendar Monday can silently shift
      // back a day and resolve to the wrong PayrollPeriod. That mismatch
      // is why the breakdown fetch could come back with zeroed totals
      // (Payroll.find on the wrong payPeriodStart matches nothing) even
      // though the line items list (fetched separately via GET /payroll)
      // showed real data for the same row.
      //
      // In single-period view, selectedMonday IS the period being shown,
      // so it's the correct value to send. In multi-period view (period
      // .count set), there's no single Monday to target — fall back to
      // period.start as before, since a proper multi-period breakdown
      // endpoint doesn't exist yet.
      const targetMonday = period.count ? period.start : selectedMonday;

      const res = await axios.post(
        `${API}/payroll/breakdown`,
        {
          payPeriodStart: targetMonday,
          userIds: [userId],
        },
        { headers: headers() },
      );

      const fullBreakdown = res.data.data?.[userId] || null;

      if (fullBreakdown) {
        const filtered = {
          ...fullBreakdown,
          hourly: payType === "hourly" ? fullBreakdown.hourly || [] : [],
          pieceworker:
            payType === "pieceworker" ? fullBreakdown.pieceworker || [] : [],
          salary: payType === "salary" ? fullBreakdown.salary : null,
          subcontractor:
            payType === "subcontractor"
              ? fullBreakdown.subcontractor || []
              : [],
        };
        setBreakdownData((prev) => ({ ...prev, [rowKey]: filtered }));
      } else {
        setBreakdownData((prev) => ({ ...prev, [rowKey]: null }));
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to load breakdown: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert((p) => ({ ...p, open: false })),
      });
    } finally {
      setBreakdownLoading((prev) => ({ ...prev, [rowKey]: false }));
    }
  };

  const savePaymentMethod = async (
    record,
    paymentMethod,
    chequeNumber = "",
  ) => {
    setPaymentSaving(true);
    try {
      const res = await axios.patch(
        `${API}/payroll/payment-method`,
        {
          weekStart: selectedMonday,
          userId: record._id,
          payType: record.payType,
          paymentMethod,
          chequeNumber,
        },
        { headers: headers() },
      );
      // Update locally instead of refetching so the table doesn't flash
      setPayrollReport((prev) =>
        prev.map((r) =>
          String(r._id) === String(record._id) && r.payType === record.payType
            ? {
                ...r,
                paymentMethod: res.data.paymentMethod,
                chequeNumber: res.data.chequeNumber,
              }
            : r,
        ),
      );
      setChequeModal({ open: false, record: null, value: "" });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Could Not Update Payment Method",
        message: err.response?.data?.message || err.message,
        onConfirm: () => setAlert((p) => ({ ...p, open: false })),
      });
    } finally {
      setPaymentSaving(false);
    }
  };

  const openChequeModal = (record) =>
    setChequeModal({
      open: true,
      record,
      value: record.chequeNumber || "",
    });

  const handlePaymentMethodChange = (record, method) => {
    if (method === "cheque") {
      // Don't switch until a cheque number is saved from the modal
      openChequeModal(record);
    } else {
      savePaymentMethod(record, "direct_deposit");
    }
  };

  // ── Remove a single line item from the draft period ─────────────────
  const removeLine = async (lineId) => {
    try {
      await axios.delete(`${API}/payroll/line/${lineId}`, {
        headers: headers(),
      });
      await fetchPayrollReport(selectedMonday);
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Remove Failed",
        message: err.response?.data?.message || err.message,
        onConfirm: () => setAlert((p) => ({ ...p, open: false })),
      });
    }
  };

  // ── Lock / unlock the currently-viewed period ────────────────────────
  const completePeriod = async () => {
    setLockConfirm(false);
    setLoading(true);
    try {
      await axios.post(
        `${API}/payroll/complete`,
        { weekStart: selectedMonday },
        { headers: headers() },
      );
      await fetchPayrollReport(selectedMonday);
      fetchUnlockedPeriods();
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Could Not Complete Period",
        message: err.response?.data?.message || err.message,
        onConfirm: () => setAlert((p) => ({ ...p, open: false })),
      });
    } finally {
      setLoading(false);
    }
  };

  const unlockPeriod = async () => {
    setUnlockConfirm(false);
    setLoading(true);
    try {
      await axios.post(
        `${API}/payroll/unlock`,
        { weekStart: selectedMonday },
        { headers: headers() },
      );
      await fetchPayrollReport(selectedMonday);
      fetchUnlockedPeriods();
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Could Not Unlock Period",
        message: err.response?.data?.message || err.message,
        onConfirm: () => setAlert((p) => ({ ...p, open: false })),
      });
    } finally {
      setLoading(false);
    }
  };

  // Fixed-position filter dropdown coordinates, keyed by which column's
  // filter is open. Using position: fixed (computed from the trigger
  // button's bounding rect) instead of position: absolute inside the <th>
  // means the panel floats above the table in a portal-like way and never
  // gets clipped or visually reflowed when filtering changes the row
  // count / table height. Recomputed every time a filter button is
  // clicked.

  const openFilterDropdownAt = (e, key, isOpenAlready) => {
    if (isOpenAlready) {
      setOpenFilterColumn(null);
      setFilterDropdownPos(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterDropdownPos({
      top: rect.bottom + 4,
      left: rect.left,
    });
    setOpenFilterColumn(key);
  };
  const openReportFilterDropdownAt = (e, key, isOpenAlready) => {
    if (isOpenAlready) {
      setOpenReportFilterColumn(null);
      setReportFilterDropdownPos(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setReportFilterDropdownPos({ top: rect.bottom + 4, left: rect.left });
    setOpenReportFilterColumn(key);
  };

  const openPicker = async (kind, weekOverride) => {
    const targetWeek = weekOverride ?? addTargetWeek;
    setPickerKind(kind);
    setPickerSelected(new Set());
    setPickerColumnFilters({});
    setOpenFilterColumn(null);
    setPickerSort({ key: null, dir: "asc" });
    setSalaryHourOverrides({});
    setReimburseToOverrides({});
    setPickerOpen(true);
    setPickerLoading(true);
    try {
      const endpoint =
        kind === "timesheet"
          ? "available/timesheets"
          : kind === "piecework"
            ? "available/piecework"
            : kind === "bill"
              ? "available/bills"
              : kind === "agreement"
                ? "available/agreements"
                : kind === "expense"
                  ? "available/expenses"
                  : "available/salary";

      const res = await axios.get(`${API}/payroll/${endpoint}`, {
        headers: headers(),
        params: kind === "salary" ? { weekStart: targetWeek } : {},
      });
      setPickerItems(res.data.items || []);

      if (kind === "expense") {
        const usersRes = await axios.get(`${API}/payroll/reimbursable-users`, {
          headers: headers(),
        });
        setReimbursableUsers(usersRes.data.users || []);
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Load Failed",
        message: err.response?.data?.message || err.message,
        onConfirm: () => setAlert((p) => ({ ...p, open: false })),
      });
      setPickerItems([]);
    } finally {
      setPickerLoading(false);
    }
  };

  // ── Union Dues picker: fetch status for the month the target period falls in ──
  // ── Union Dues picker: fetch status for the month the target period falls in ──
  const openUnionDuesPicker = async (weekOverride) => {
    const targetWeek = weekOverride ?? addTargetWeek;
    if (!targetWeek) return;

    setPickerKind("uniondues");
    setPickerSelected(new Set());
    setPickerColumnFilters({});
    setOpenFilterColumn(null);
    setPickerSort({ key: null, dir: "asc" });
    setDuesMethod("dollars");
    setPickerOpen(true);
    setPickerLoading(true);
    try {
      const anchorDate = new Date(targetWeek);
      const year = anchorDate.getFullYear();
      const month = anchorDate.getMonth() + 1; // 1-12

      const res = await axios.get(`${API}/payroll/union-dues/status`, {
        headers: headers(),
        params: { year, month },
      });

      // Show everyone — already-charged and unconfigured workers stay
      // visible (disabled) so the modal doubles as a "who's been charged
      // this month" view instead of hiding that information.
      setPickerItems(res.data.items || []);
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Load Failed",
        message: err.response?.data?.message || err.message,
        onConfirm: () => setAlert((p) => ({ ...p, open: false })),
      });
      setPickerItems([]);
    } finally {
      setPickerLoading(false);
    }
  };

  // ── Submit selected dues charges ──
  const submitUnionDuesPicker = async () => {
    if (pickerSelected.size === 0 || !addTargetWeek) return;

    if (duesMethod === "hours") {
      const chosenItems = pickerItems.filter((i) =>
        pickerSelected.has(i.userId),
      );
      const blocked = chosenItems.filter((i) => !(i.payRate > 0));
      if (blocked.length > 0) {
        setAlert({
          open: true,
          type: "error",
          title: "Can't Charge in Hours",
          message: `${blocked.length} selected worker(s) have no pay rate set, so dues can't be converted to hours. Switch to Dollars or deselect them.`,
          onConfirm: () => setAlert((p) => ({ ...p, open: false })),
        });
        return;
      }
    }

    setPickerSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/payroll/union-dues/charge`,
        {
          weekStart: addTargetWeek,
          duesMethod, // ← added
          userIds: Array.from(pickerSelected),
        },
        { headers: headers() },
      );

      closePicker();

      if (res.data.skippedCount > 0) {
        setAlert({
          open: true,
          type: "error",
          title: "Some Dues Not Charged",
          message: `${res.data.chargedCount} charged, ${res.data.skippedCount} skipped (already charged this month or missing config).`,
          onConfirm: () => setAlert((p) => ({ ...p, open: false })),
        });
      }

      if (selectedMonday === addTargetWeek && viewMode === "report") {
        await fetchPayrollReport(addTargetWeek);
      } else {
        setSelectedMonday(addTargetWeek);
        await fetchPayrollReport(addTargetWeek);
      }
      fetchUnlockedPeriods();
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Charge Failed",
        message: err.response?.data?.message || err.message,
        onConfirm: () => setAlert((p) => ({ ...p, open: false })),
      });
    } finally {
      setPickerSubmitting(false);
    }
  };

  const closePicker = () => {
    setPickerOpen(false);
    setPickerKind(null);
    setPickerItems([]);
    setPickerSelected(new Set());
    setPickerColumnFilters({});
    setOpenFilterColumn(null);
    setPickerSort({ key: null, dir: "asc" });
    setPickerLockedToPeriod(false);
  };

  const pickerColumnOptions = (colKey) => {
    const cols = PICKER_COLUMNS[pickerKind] || [];
    const col = cols.find((c) => c.key === colKey);
    if (!col) return [];

    const norm = (v) => (v == null || v === "" ? "—" : String(v));

    // Items that pass every OTHER column's filter.
    const rows = pickerItems.filter((item) => {
      for (const other of cols) {
        if (other.key === colKey) continue;
        const fv = pickerColumnFilters[other.key];
        if (!(fv instanceof Set) || fv.size === 0) continue;
        if (!fv.has(norm(other.get(item)))) return false;
      }
      return true;
    });

    const selected =
      pickerColumnFilters[colKey] instanceof Set
        ? pickerColumnFilters[colKey]
        : new Set();

    // Date columns: sort by the underlying timestamp, newest first.
    if (col.isDate && col.rawGet) {
      const displayToTimestamp = new Map();
      rows.forEach((i) => {
        const key = norm(col.get(i));
        const raw = col.rawGet(i);
        const ts = raw ? new Date(raw).getTime() : -Infinity;
        const existing = displayToTimestamp.get(key);
        if (existing === undefined || ts > existing) {
          displayToTimestamp.set(key, ts);
        }
      });
      selected.forEach((v) => {
        if (!displayToTimestamp.has(v)) displayToTimestamp.set(v, -Infinity);
      });
      return Array.from(displayToTimestamp.keys()).sort(
        (a, b) => displayToTimestamp.get(b) - displayToTimestamp.get(a),
      );
    }

    const values = new Set(rows.map((i) => norm(col.get(i))));
    selected.forEach((v) => values.add(v));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  };

  const setTextFilter = (colKey, value) => {
    setPickerColumnFilters((prev) => ({ ...prev, [colKey]: value }));
  };

  const toggleChoiceFilterValue = (colKey, value) => {
    setPickerColumnFilters((prev) => {
      const current =
        prev[colKey] instanceof Set ? new Set(prev[colKey]) : new Set();
      if (current.has(value)) current.delete(value);
      else current.add(value);
      return { ...prev, [colKey]: current };
    });
  };

  const clearColumnFilter = (colKey) => {
    setPickerColumnFilters((prev) => {
      const next = { ...prev };
      delete next[colKey];
      return next;
    });
  };

  const clearAllPickerFilters = () => setPickerColumnFilters({});

  const isColumnFilterActive = (colKey) => {
    const v = pickerColumnFilters[colKey];
    if (v == null) return false;
    if (v instanceof Set) return v.size > 0;
    return String(v).trim().length > 0;
  };

  const filteredPickerItems = useMemo(() => {
    const cols = PICKER_COLUMNS[pickerKind] || [];
    return pickerItems.filter((item) => {
      for (const col of cols) {
        const filterVal = pickerColumnFilters[col.key];
        if (filterVal == null) continue;
        const rawVal = col.get(item);
        const itemVal = rawVal == null || rawVal === "" ? "—" : String(rawVal);
        if (col.type === "text") {
          const q = String(filterVal).trim().toLowerCase();
          if (!q) continue;
          if (!itemVal.toLowerCase().includes(q)) return false;
        } else {
          // choice
          if (!(filterVal instanceof Set) || filterVal.size === 0) continue;
          if (!filterVal.has(itemVal)) return false;
        }
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerItems, pickerColumnFilters, pickerKind]);

  // Sort accessor covering both the filterable PICKER_COLUMNS entries
  // (worker, logNumber, woNumber, etc.) and the extra value-only columns
  // that appear per picker kind but aren't filterable (hours, subtotal,
  // tax, total, configuredDuesAmount, deduction). Returns a raw
  // comparable value (number or string) — numeric columns stay numeric
  // so 2 sorts before 10, not after.
  const pickerSortValue = (item, key) => {
    // Date columns need their raw timestamp for correct chronological
    // sort — PICKER_COLUMNS' `get()` returns a display string
    // (toLocaleDateString) for filtering, which would sort alphabetically
    // instead of chronologically if used here.
    if (key === "workCompletedDate") {
      return item.workCompletedDate
        ? new Date(item.workCompletedDate).getTime()
        : 0;
    }
    if (key === "approvedAt") {
      return item.approvedAt ? new Date(item.approvedAt).getTime() : 0;
    }

    const col = (PICKER_COLUMNS[pickerKind] || []).find((c) => c.key === key);
    if (col) {
      const v = col.get(item);
      return v == null || v === "" ? "" : v;
    }
    switch (key) {
      case "hours":
        return item.hours ?? 0;
      case "lineItemCount":
        return item.lineItemCount ?? 0;
      case "subtotal":
        return item.subtotal ?? 0;
      case "tax":
        return item.tax ?? 0;
      case "total":
        return item.total ?? item.amount ?? 0;
      case "configuredDuesAmount":
        return item.configuredDuesAmount ?? 0;
      case "deduction": {
        if (item.alreadyCharged) return item.chargedAmount ?? 0;
        const p = previewDuesDeduction(item, duesMethod);
        return p.blocked ? -1 : p.amountDeducted;
      }
      default:
        return "";
    }
  };

  const sortedPickerItems = useMemo(() => {
    if (!pickerSort.key) return filteredPickerItems;
    const { key, dir } = pickerSort;
    const factor = dir === "asc" ? 1 : -1;
    return [...filteredPickerItems].sort((a, b) => {
      const va = pickerSortValue(a, key);
      const vb = pickerSortValue(b, key);
      if (typeof va === "number" && typeof vb === "number") {
        return (va - vb) * factor;
      }
      return (
        String(va).localeCompare(String(vb), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * factor
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPickerItems, pickerSort, pickerKind, duesMethod]);

  const togglePickerSort = (key) => {
    setPickerSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: "asc" }; // third click clears sort
    });
  };

  const togglePickerItem = (id) => {
    if (pickerKind === "uniondues") {
      const item = pickerItems.find((i) => i.userId === id);
      if (item && isDuesRowLocked(item)) return;
    }
    setPickerSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllPicker = () =>
    setPickerSelected(
      new Set(
        sortedPickerItems
          .filter((i) => pickerKind !== "uniondues" || !isDuesRowLocked(i))
          .map((i) => i._id || i.userId),
      ),
    );
  const clearPickerSelection = () => setPickerSelected(new Set());

  const submitPicker = async () => {
    if (pickerSelected.size === 0) return;
    if (pickerKind === "uniondues") return submitUnionDuesPicker();
    if (!addTargetWeek) {
      setAlert({
        open: true,
        type: "error",
        title: "No Period Selected",
        message: "Choose a pay period to add these items to.",
        onConfirm: () => setAlert((p) => ({ ...p, open: false })),
      });
      return;
    }

    if (pickerKind === "expense") {
      const missing = Array.from(pickerSelected).filter(
        (id) =>
          !reimburseToOverrides[id] &&
          !pickerItems.find((i) => i._id === id)?.userId,
      );
      if (missing.length > 0) {
        setAlert({
          open: true,
          type: "error",
          title: "Missing Reimburse-To",
          message: `${missing.length} selected item${missing.length === 1 ? "" : "s"} still need a worker chosen to reimburse.`,
          onConfirm: () => setAlert((p) => ({ ...p, open: false })),
        });
        return;
      }
    }

    setPickerSubmitting(true);
    try {
      let res;
      if (pickerKind === "salary") {
        const entries = Array.from(pickerSelected).map((userId) => ({
          userId,
          hours:
            salaryHourOverrides[userId] != null
              ? Number(salaryHourOverrides[userId])
              : undefined,
        }));
        res = await axios.post(
          `${API}/payroll/add-salary`,
          { weekStart: addTargetWeek, entries },
          { headers: headers() },
        );
      } else if (pickerKind === "expense") {
        const items = Array.from(pickerSelected).map((id) => ({
          id,
          userId:
            reimburseToOverrides[id] ||
            pickerItems.find((i) => i._id === id)?.userId ||
            null,
        }));
        res = await axios.post(
          `${API}/payroll/add`,
          { weekStart: addTargetWeek, kind: "expense", items },
          { headers: headers() },
        );
      } else {
        res = await axios.post(
          `${API}/payroll/add`,
          {
            weekStart: addTargetWeek,
            kind: pickerKind,
            ids: Array.from(pickerSelected),
          },
          { headers: headers() },
        );
      }

      closePicker();
      if (selectedMonday === addTargetWeek && viewMode === "report") {
        await fetchPayrollReport(addTargetWeek);
      } else {
        setSelectedMonday(addTargetWeek);
        await fetchPayrollReport(addTargetWeek);
      }
      fetchUnlockedPeriods();

      // Surface skips AFTER the refresh (fetchPayrollReport closes alerts)
      if (res.data.skippedCount > 0) {
        setAlert({
          open: true,
          type: "error",
          title:
            res.data.addedCount === 0
              ? "Nothing Was Added"
              : "Some Items Not Added",
          message: `${res.data.addedCount} added, ${res.data.skippedCount} skipped (no longer eligible, or no reimburse-to worker).`,
          onConfirm: () => setAlert((p) => ({ ...p, open: false })),
        });
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Add Failed",
        message: err.response?.data?.message || err.message,
        onConfirm: () => setAlert((p) => ({ ...p, open: false })),
      });
    } finally {
      setPickerSubmitting(false);
    }
  };

  // ── Wagepoint-style Hourly + Salary export (hours in Regular Pay) ───────
  const handleExportHourlyCSV = async () => {
    const startPeriod = calculatePeriodDates(exportStartWeek);
    const endPeriod = calculatePeriodDates(exportEndWeek);

    if (!startPeriod || !endPeriod) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Please select both start and end weeks for export.",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    setLoading(true);
    setAlert((prev) => ({ ...prev, open: false }));

    try {
      const payrollResponse = await axios.get(`${API}/payroll`, {
        headers: headers(),
        params: { weekStart: startPeriod.start, weekEnd: endPeriod.end },
      });

      // Only hourly + salary belong in the Wagepoint hourly export
      // Include hourly + salary (added) AND uniondues (subtracted, hours-only) —
      // dollars-based dues intentionally excluded, they don't touch hours.
      const payrollData = payrollResponse.data.payrollReport.filter(
        (record) => {
          const types = Array.isArray(record.payType)
            ? record.payType.map((t) => t.toLowerCase())
            : [record.payType?.toLowerCase()];
          return types.some((t) =>
            ["hourly", "salary", "uniondues"].includes(t),
          );
        },
      );

      const aggregatedData = payrollData.reduce((acc, record) => {
        const key = record.employeeNumber || record.displayName || "Unknown";
        if (!acc[key]) {
          const nameParts = (record.displayName || "").split(" ");
          acc[key] = {
            displayName: record.displayName || "",
            employeeNumber: record.employeeNumber || "",
            firstName: nameParts[0] || "",
            lastName: nameParts.slice(1).join(" ") || "",
            regularHours: 0,
          };
        }

        const payType = record.payType?.toLowerCase();

        if (payType === "uniondues") {
          // Only hours-based dues carry a nonzero totalHours (always negative
          // by construction in /union-dues/charge). Dollars-based dues have
          // totalHours 0, so this is a no-op for them automatically.
          acc[key].regularHours += Number(record.hoursWorked || 0);
          return acc;
        }

        const hours =
          payType === "salary"
            ? Number(record.hoursPerWeek ?? record.hoursWorked ?? 0)
            : Number(record.hoursWorked || 0);
        acc[key].regularHours += hours;
        return acc;
      }, {});
      const runExport = () => {
        const headerRow = [
          "Employee name",
          "Employee payroll ID",
          "First name",
          "Last name",
          "job id",
          "Department name",
          "Overtime",
          "Regular Pay",
          "Sick Pay",
          "Time Worked on Stat Holiday",
          "Vacation Pay",
          "External ID",
        ];

        const rows = Object.values(aggregatedData).map((employee) =>
          [
            employee.displayName,
            employee.employeeNumber || "N/A",
            employee.firstName,
            employee.lastName,
            0,
            "",
            0,
            employee.regularHours.toFixed(2),
            0,
            "",
            0,
            "",
          ]
            .map((field) => `"${field}"`)
            .join(","),
        );

        const csvContent = [
          `"Summary Report - Time by Bldera"`,
          `${formatDate(startPeriod.start)}-${formatDate(endPeriod.end)}`,
          "",
          headerRow.join(","),
          ...rows,
        ].join("\n");

        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `hourly_export_${startPeriod.start}_to_${endPeriod.end}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsExportModalOpen(false);
        setExportStep(1);
        setExportType("");
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "Hourly payroll exported successfully!",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
      };

      withEmployeeIdCheck(Object.values(aggregatedData), runExport);
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to export hourly payroll: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Earnings export: piecework + subcontractor (+ their expense reimbursements) ──
  // ── Earnings export: piecework + subcontractor (+ expense reimbursements) ──
  const handleExportCSV = async () => {
    if (!exportStartWeek || !exportEndWeek) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Please select both start and end weeks for export.",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    setLoading(true);
    setAlert((prev) => ({ ...prev, open: false }));

    try {
      const companyResponse = await axios.get(`${API}/company-profile`, {
        headers: headers(),
      });
      const companyName = companyResponse.data.companyName || "Unknown Company";

      const startPeriod = calculatePeriodDates(exportStartWeek);
      const endPeriod = calculatePeriodDates(exportEndWeek);
      if (!startPeriod || !endPeriod) throw new Error("Invalid week selection");

      const payrollResponse = await axios.get(`${API}/payroll`, {
        headers: headers(),
        params: { weekStart: startPeriod.start, weekEnd: endPeriod.end },
      });

      const allRows = payrollResponse.data.payrollReport || [];
      const typeOf = (r) => String(r.payType || "").toLowerCase();

      // Expenses for these workers are exported in another file:
      //  - salaried workers → Rippling salary export
      //  - hourly-type site workers → hourly workers export
      const salaryUserIds = new Set(
        allRows.filter((r) => typeOf(r) === "salary").map((r) => String(r._id)),
      );

      // Piecework/subcontractor rows, plus expenses for everyone whose
      // expenses aren't exported elsewhere (incl. overhead reimbursements
      // for hourly/piecework workers)
      const payrollData = allRows.filter((r) => {
        const t = typeOf(r);
        if (["pieceworker", "subcontractor"].includes(t)) return true;
        return (
          t === "expense" &&
          !salaryUserIds.has(String(r._id)) &&
          !isHourlyWorkerRow(r)
        );
      });

      const aggregatedData = payrollData.reduce((acc, record) => {
        const key = record.employeeNumber || record.displayName || "Unknown";
        if (!acc[key]) {
          const nameParts = (record.displayName || "").split(" ");
          acc[key] = {
            displayName: record.displayName || "",
            employeeNumber: record.employeeNumber || "",
            firstName: nameParts[0] || "",
            lastName: nameParts.slice(1).join(" ") || "",
            amount: 0,
            tax: 0,
            expense: 0,
          };
        }
        if (typeOf(record) === "expense") {
          // Worker paid the HST themselves: reimburse the full total,
          // and don't report the tax in the HST column.
          acc[key].expense +=
            parseFloat(record.amount || 0) + parseFloat(record.tax || 0);
        } else {
          acc[key].amount += parseFloat(record.amount || 0);
          acc[key].tax += parseFloat(record.tax || 0);
        }
        return acc;
      }, {});

      const runExport = () => {
        const headerRow = [
          "Employee name",
          "Employee payroll ID",
          "First name",
          "Last name",
          "job id",
          "Department Name",
          "Bonus Discretionary",
          "Bonus Work related",
          "Contractor mileage (taxable)",
          "Contractors",
          "Employee mileage (non taxable)",
          "Expense Reimbursement",
          "HST",
          "Stat Holiday",
          "External ID",
          "Job Title",
        ];

        const rows = Object.values(aggregatedData).map((employee) =>
          [
            employee.displayName,
            employee.employeeNumber || "N/A",
            employee.firstName,
            employee.lastName,
            0,
            "",
            "",
            "",
            "",
            employee.amount.toFixed(2),
            "",
            employee.expense ? employee.expense.toFixed(2) : "",
            employee.tax.toFixed(2),
            "",
            "",
            "",
          ]
            .map((field) => `"${field}"`)
            .join(","),
        );

        const csvContent = [
          `"Earnings Import File - ${companyName}"`,
          "",
          headerRow.join(","),
          ...rows,
        ].join("\n");

        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `payroll_export_${startPeriod.start}_to_${endPeriod.end}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsExportModalOpen(false);
        setExportStep(1);
        setExportType("");
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "Payroll exported successfully!",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
      };

      withEmployeeIdCheck(Object.values(aggregatedData), runExport);
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to export payroll: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchExportRangeReport = async () => {
    const startPeriod = calculatePeriodDates(exportStartWeek);
    const endPeriod = calculatePeriodDates(exportEndWeek);
    if (!startPeriod || !endPeriod) {
      throw new Error("Please select both start and end weeks for export.");
    }
    const res = await axios.get(`${API}/payroll`, {
      headers: headers(),
      params: { weekStart: startPeriod.start, weekEnd: endPeriod.end },
    });
    return { startPeriod, endPeriod, rows: res.data.payrollReport || [] };
  };

  const finishRipplingExport = (csv, filename, message) => {
    downloadCSVFile(csv, filename);
    setIsExportModalOpen(false);
    setExportStep(1);
    setExportType("");
    setAlert({
      open: true,
      type: "success",
      title: "Success",
      message,
      onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
    });
  };

  // ── Rippling: Hourly + Piecework (+ expenses, union dues) ───────────────
  // ── Rippling: Hourly + Piecework (+ expenses, union dues) ───────────────
  // ── Rippling: Hourly + Piecework (+ expenses, union dues) ───────────────
  const handleExportRipplingHourlyCSV = async () => {
    setLoading(true);
    setAlert((prev) => ({ ...prev, open: false }));
    try {
      const {
        startPeriod,
        endPeriod,
        rows: allRows,
      } = await fetchExportRangeReport();

      // Hourly-type site workers go in their own Excel upload
      const rows = allRows.filter((r) => !isHourlyWorkerRow(r));

      const relevant = [
        "hourly",
        "expense",
        "uniondues",
        ...CONTRACTOR_PAY_TYPES,
      ];

      // Expenses for salaried workers go in the salary file, not this one
      const salaryUserIds = new Set(
        rows
          .filter((r) => r.payType?.toLowerCase() === "salary")
          .map((r) => String(r._id)),
      );

      const aggregated = rows
        .filter(
          (r) =>
            relevant.includes(r.payType?.toLowerCase()) &&
            !(
              r.payType?.toLowerCase() === "expense" &&
              salaryUserIds.has(String(r._id))
            ),
        )
        .reduce((acc, r) => {
          const key = String(r._id || r.displayName || "Unknown");
          if (!acc[key]) {
            acc[key] = {
              displayName: r.displayName || "",
              ripplingEmployeeNumber: r.ripplingEmployeeNumber || "",
              hourlyHours: 0,
              hourlyRate: 0,
              hourlyAmount: 0,
              contractor: 0,
              expense: 0,
              hst: 0,
              unionDues: 0,
            };
          }
          const e = acc[key];
          const type = r.payType?.toLowerCase();
          const amount = parseFloat(r.amount || 0);
          const tax = parseFloat(r.tax || 0);

          if (type === "hourly") {
            e.hourlyHours += parseFloat(r.hoursWorked || 0);
            e.hourlyAmount += amount;
            e.hourlyRate = r.payRate || e.hourlyRate;
            e.hst += tax;
          } else if (CONTRACTOR_PAY_TYPES.includes(type)) {
            e.contractor += amount;
            e.hst += tax;
          } else if (type === "expense") {
            // Worker paid the HST themselves, so reimburse the full total
            // and don't report the tax in the HST column.
            e.expense += amount + tax;
          } else if (type === "uniondues") {
            e.unionDues += Math.abs(amount);
          }
          return acc;
        }, {});

      const employees = Object.values(aggregated);
      if (employees.length === 0) {
        throw new Error(
          "No hourly, piecework or expense records in this range.",
        );
      }

      const fmt = (n) => (n ? Number(n).toFixed(2) : "");

      const runExport = () => {
        const csvRows = employees.map((e) => ({
          "Rippling Emp No": e.ripplingEmployeeNumber || "N/A",
          "Employee Name": e.displayName,
          "Hourly Hours": fmt(e.hourlyHours),
          "Hourly Rate": e.hourlyHours > 0 ? fmt(e.hourlyRate) : "",
          "Hourly Amount": fmt(e.hourlyAmount),
          "Contractor Payments": fmt(e.contractor),
          "Expense Reimbursement": fmt(e.expense),
          HST: fmt(e.hst),
          "Union Dues Employee Employee Contribution": fmt(e.unionDues),
        }));
        finishRipplingExport(
          buildRipplingCSV(RIPPLING_HOURLY_HEADERS, csvRows),
          `rippling_hourly_piecework_${startPeriod.start}_to_${endPeriod.end}.csv`,
          "Rippling hourly + piecework export created successfully!",
        );
      };

      // The missing-ID prompt must check the Rippling number, not employeeNumber
      withEmployeeIdCheck(
        employees.map((e) => ({
          displayName: e.displayName,
          employeeNumber: e.ripplingEmployeeNumber,
        })),
        runExport,
      );
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to export Rippling hourly/piecework: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Rippling: Salary (+ salaried workers' expense reimbursements) ───────
  // ── Rippling: Salary (+ salaried workers' expense reimbursements) ───────
  const handleExportRipplingSalaryCSV = async () => {
    setLoading(true);
    setAlert((prev) => ({ ...prev, open: false }));
    try {
      const {
        startPeriod,
        endPeriod,
        rows: allRows,
      } = await fetchExportRangeReport();

      // Hourly-type site workers go in their own Excel upload
      const rows = allRows.filter((r) => !isHourlyWorkerRow(r));

      const salaryUserIds = new Set(
        rows
          .filter((r) => r.payType?.toLowerCase() === "salary")
          .map((r) => String(r._id)),
      );

      const aggregated = rows
        .filter((r) => {
          const t = r.payType?.toLowerCase();
          return (
            t === "salary" ||
            (t === "expense" && salaryUserIds.has(String(r._id)))
          );
        })
        .reduce((acc, r) => {
          const key = String(r._id || r.displayName || "Unknown");
          if (!acc[key]) {
            acc[key] = {
              displayName: r.displayName || "",
              ripplingEmployeeNumber: r.ripplingEmployeeNumber || "",
              hours: 0,
              amount: 0,
              expense: 0,
            };
          }
          const type = r.payType?.toLowerCase();
          if (type === "expense") {
            // Full total including the HST they paid
            acc[key].expense +=
              parseFloat(r.amount || 0) + parseFloat(r.tax || 0);
          } else {
            acc[key].hours += parseFloat(r.hoursWorked || 0);
            acc[key].amount += parseFloat(r.amount || 0);
          }
          return acc;
        }, {});

      const employees = Object.values(aggregated);
      if (employees.length === 0) {
        throw new Error("No salary records in this range.");
      }

      const runExport = () => {
        const csvRows = employees.map((e) => ({
          "Rippling Emp No": e.ripplingEmployeeNumber || "N/A",
          "Employee Name": e.displayName,
          "Salary Hours": e.hours.toFixed(2),
          "Salary Amount": e.amount.toFixed(2),
          "Expense Reimbursement": e.expense ? e.expense.toFixed(2) : "",
        }));
        finishRipplingExport(
          buildRipplingCSV(RIPPLING_SALARY_HEADERS, csvRows),
          `rippling_salary_${startPeriod.start}_to_${endPeriod.end}.csv`,
          "Rippling salary export created successfully!",
        );
      };

      withEmployeeIdCheck(
        employees.map((e) => ({
          displayName: e.displayName,
          employeeNumber: e.ripplingEmployeeNumber,
        })),
        runExport,
      );
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to export Rippling salary: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setLoading(false);
    }
  };

  // Union Dues (Excel): the month is chosen in the export modal; this hands off
  // to UnionDuesExportModal, where the admin filters/ticks the payroll entries
  // to remit and the server fills the DALI Hourly (ICI) + Piecework workbooks.
  const handleExportUnionDues = () => {
    if (!selectedMonth) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Please select a month for Union Dues export.",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    setAlert((prev) => ({ ...prev, open: false }));
    setIsExportModalOpen(false);
    setExportStep(1);
    setDuesExportOpen(true);
  };

  const openExportModal = (weekOverride) => {
    const anchor =
      typeof weekOverride === "string"
        ? weekOverride
        : (period?.start ?? selectedMonday);
    const range = calculatePeriodDates(anchor);

    setIsExportModalOpen(true);
    setExportStep(1);
    setExportType("");
    setExportRangeMode("week");
    setExportAnchorWeek(anchor);
    setExportStartWeek(anchor);
    setExportEndWeek(range ? range.end : anchor);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeExportModal = () => {
    setIsExportModalOpen(false);
    setExportStep(1);
    setExportType("");
    setSelectedUnion("all");
    setExportRangeMode("week");
  };

  const targetWeekLabel = (mondayISO) => {
    const wk = weekPeriods.find((w) => w.value === mondayISO);
    return wk ? `${formatDate(wk.start)} - ${formatDate(wk.end)}` : mondayISO;
  };

  const handleNextStep = () => {
    if (exportStep === 1 && !exportType) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Please select an export type.",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    setAlert((prev) => ({ ...prev, open: false }));
    setExportStep(2);
  };

  // ── CSV: hourly site workers — separate Rippling upload ─────────────────
  const HOURLY_WORKERS_HEADERS = [
    "Rippling Emp No",
    "Employee Name",
    "Hourly Hours",
    "Hourly Rate",
    "Hourly Amount",
    "HST",
    "Expense Reimbursement",
    "Union Dues ($)",
    "Union Dues (Hrs)",
    "Other Earnings",
  ];

  const handleExportHourlyWorkersCSV = async () => {
    setLoading(true);
    setAlert((prev) => ({ ...prev, open: false }));
    try {
      const { startPeriod, endPeriod, rows } = await fetchExportRangeReport();

      const aggregated = rows.filter(isHourlyWorkerRow).reduce((acc, r) => {
        const key = String(r._id || r.displayName || "Unknown");
        if (!acc[key]) {
          acc[key] = {
            ripplingEmployeeNumber: r.ripplingEmployeeNumber || "",
            displayName: r.displayName || "",
            hours: 0,
            rate: r.payRate || 0,
            hourlyAmount: 0,
            hst: 0,
            expense: 0,
            duesAmount: 0,
            duesHours: 0,
            other: 0,
          };
        }
        const e = acc[key];
        const type = r.payType?.toLowerCase();
        const amount = parseFloat(r.amount || 0);
        const tax = parseFloat(r.tax || 0);

        if (type === "hourly") {
          e.hours += parseFloat(r.hoursWorked || 0);
          e.hourlyAmount += amount;
          e.hst += tax;
        } else if (type === "expense") {
          // Worker paid the HST themselves: reimburse the full total
          e.expense += amount + tax;
        } else if (type === "uniondues") {
          e.duesAmount += Math.abs(amount);
          e.duesHours += Math.abs(parseFloat(r.hoursWorked || 0));
        } else {
          // piecework / subcontractor / salary, if any exist for this worker
          e.other += parseFloat(r.totalPay || 0);
        }
        return acc;
      }, {});

      const employees = Object.values(aggregated).sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      );
      if (employees.length === 0) {
        throw new Error(
          "No records for hourly site workers in this range. Check that workers are set to Payment Type: Hourly and have items added to the period.",
        );
      }

      const fmt = (n) => (n ? Number(n).toFixed(2) : "");

      const runExport = () => {
        const csvRows = employees.map((e) => ({
          "Rippling Emp No": e.ripplingEmployeeNumber || "N/A",
          "Employee Name": e.displayName,
          "Hourly Hours": fmt(e.hours),
          "Hourly Rate": e.hours > 0 ? fmt(e.rate) : "",
          "Hourly Amount": fmt(e.hourlyAmount),
          HST: fmt(e.hst),
          "Expense Reimbursement": fmt(e.expense),
          "Union Dues ($)": fmt(e.duesAmount),
          "Union Dues (Hrs)": fmt(e.duesHours),
          "Other Earnings": fmt(e.other),
        }));
        finishRipplingExport(
          buildRipplingCSV(HOURLY_WORKERS_HEADERS, csvRows),
          `rippling_hourly_workers_${startPeriod.start}_to_${endPeriod.end}.csv`,
          "Hourly workers export created successfully!",
        );
      };

      // This is a Rippling upload, so the missing-ID prompt checks the Rippling number
      withEmployeeIdCheck(
        employees.map((e) => ({
          displayName: e.displayName,
          employeeNumber: e.ripplingEmployeeNumber,
        })),
        runExport,
      );
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to export hourly workers: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (exportType === "csv") handleExportCSV();
    else if (exportType === "hourly") handleExportHourlyCSV();
    else if (exportType === "ripplingHourly") handleExportRipplingHourlyCSV();
    else if (exportType === "ripplingSalary") handleExportRipplingSalaryCSV();
    else if (exportType === "hourlyWorkers") handleExportHourlyWorkersCSV();
    else if (exportType === "unionDues") handleExportUnionDues();
  };
  const unionOptions = unions.map((union) => ({
    value: union.title,
    label: union.title,
  }));
  const weekPeriodOptions = weekPeriods.map((week) => ({
    value: week.value,
    label: `${formatDate(week.start)} - ${formatDate(week.end)}`,
  }));
  const addTargetOptions = unlockedPeriods.map((p) => ({
    value: p.weekStart,
    label: `${formatDate(p.weekStart)} - ${formatDate(p.weekEnd)}${p.isCurrent ? " (current)" : ""}`,
  }));

  const reportTotals = sortedPayrollReport.reduce(
    (acc, r) => {
      acc.totalPay += parseFloat(r.totalPay || 0);
      acc.amount += parseFloat(r.amount || 0);
      acc.tax += parseFloat(r.tax || 0);
      acc.hours += parseFloat(r.hoursWorked || 0);
      acc.workers.add(r.employeeNumber || r.displayName);
      return acc;
    },
    { totalPay: 0, amount: 0, tax: 0, hours: 0, workers: new Set() },
  );

  if (loading)
    return (
      <div className="min-h-[240px] flex items-center justify-center bg-[#0c0e12]">
        <div className="flex items-center gap-3 text-gray-400 text-sm">
          <span
            className="w-4 h-4 rounded-full border-2 border-gray-600 animate-spin"
            style={{ borderTopColor: ACCENT }}
          />
          Loading payroll…
        </div>
      </div>
    );

  const PICKER_TITLES = {
    timesheet: "Add Approved Timesheets",
    piecework: "Add Approved Piecework Logs",
    bill: "Add Subcontractor Bills — pays on period lock",
    salary: "Add Salary Workers",
    agreement: "Add Subcontractor Agreements — pays on period lock",
    uniondues: "Charge Union Dues — deducted before pay",
    expense: "Add Employee Expense Reimbursements — pays on period lock",
  };

  return (
    <div className="mx-auto px-4 py-6 bg-[#0c0e12] rounded-xl mt-8 font-sans">
      <div
        className="rounded-xl p-5 sm:p-6"
        style={{
          background: "linear-gradient(180deg, #14171d 0%, #101318 100%)",
          border: "1px solid #22262e",
        }}
      >
        <AlertDialog
          type={alert.type}
          title={alert.title}
          message={alert.message}
          confirmText="OK"
          cancelText="Close"
          open={alert.open}
          onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
          onConfirm={alert.onConfirm}
        />

        {/* ── Complete-period confirm dialog ─────────────────────────── */}
        {lockConfirm && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div
              className="p-6 rounded-xl shadow-2xl max-w-md w-full"
              style={{
                backgroundColor: "#161a20",
                border: "1px solid #2a2f37",
              }}
            >
              <h2 className="text-base font-semibold text-white mb-2">
                Complete this pay period?
              </h2>
              <p className="text-sm text-gray-400 mb-5">
                Locking prevents any further items from being added or removed.
                You can unlock it again later if you need to make changes.
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setLockConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-300 bg-[#1c2027] rounded-lg hover:bg-[#22262e] transition-colors duration-150"
                >
                  Cancel
                </button>
                <button
                  onClick={completePeriod}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:brightness-110 transition-all duration-150"
                  style={{ backgroundColor: "#34d399" }}
                >
                  Complete &amp; Lock
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Unlock-period confirm dialog ───────────────────────────── */}
        {unlockConfirm && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div
              className="p-6 rounded-xl shadow-2xl max-w-md w-full"
              style={{
                backgroundColor: "#161a20",
                border: "1px solid #2a2f37",
              }}
            >
              <h2 className="text-base font-semibold text-white mb-2">
                Unlock this pay period?
              </h2>
              <p className="text-sm text-gray-400 mb-5">
                This reopens the period for edits — you'll be able to add or
                remove items again. Nothing that's already been added is changed
                by unlocking.
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setUnlockConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-300 bg-[#1c2027] rounded-lg hover:bg-[#22262e] transition-colors duration-150"
                >
                  Cancel
                </button>
                <button
                  onClick={unlockPeriod}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:brightness-110 transition-all duration-150"
                  style={{ backgroundColor: "#e85d24" }}
                >
                  Unlock
                </button>
              </div>
            </div>
          </div>
        )}

        {missingIdConfirm.open && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] px-4">
            <div
              className="p-6 rounded-xl shadow-2xl max-w-md w-full"
              style={{
                backgroundColor: "#161a20",
                border: "1px solid #2a2f37",
              }}
            >
              <h2 className="text-base font-semibold text-white mb-2">
                Missing Employee ID
              </h2>
              <p className="text-sm text-gray-400 mb-3">
                The following worker
                {missingIdConfirm.workers.length === 1 ? "" : "s"}{" "}
                {missingIdConfirm.workers.length === 1 ? "has" : "have"} no
                employee ID on file. They'll be exported with{" "}
                <span className="text-gray-200 font-mono">N/A</span> in that
                column.
              </p>
              <ul className="mb-5 max-h-40 overflow-y-auto text-sm text-gray-300 list-disc list-inside space-y-1">
                {missingIdConfirm.workers.map((w, i) => (
                  <li key={i}>{w.displayName}</li>
                ))}
              </ul>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={cancelMissingIdExport}
                  className="px-4 py-2 text-sm font-medium text-gray-300 bg-[#1c2027] rounded-lg hover:bg-[#22262e]"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmMissingIdExport}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:brightness-110"
                  style={{ backgroundColor: ACCENT }}
                >
                  Continue Export
                </button>
              </div>
            </div>
          </div>
        )}

        {chequeModal.open && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] px-4">
            <div
              className="p-6 rounded-xl shadow-2xl max-w-md w-full"
              style={{
                backgroundColor: "#161a20",
                border: "1px solid #2a2f37",
              }}
            >
              <h2 className="text-base font-semibold text-white mb-1">
                Pay by cheque
              </h2>
              <p className="text-xs text-gray-500 mb-4">
                {chequeModal.record?.displayName}
              </p>
              <label className="block text-xs font-medium text-gray-400 mb-2">
                Cheque number <span className="text-red-400">*</span>
              </label>
              <input
                autoFocus
                type="text"
                value={chequeModal.value}
                onChange={(e) =>
                  setChequeModal((p) => ({ ...p, value: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && chequeModal.value.trim()) {
                    savePaymentMethod(
                      chequeModal.record,
                      "cheque",
                      chequeModal.value,
                    );
                  }
                }}
                placeholder="e.g. 1042"
                className="w-full p-2 rounded-lg bg-[#0e1014] border border-[#2a2f37] text-gray-200 focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm outline-none font-mono"
              />
              <p className="text-[11px] text-gray-500 mt-2 mb-5">
                Required. If you don't have the real number yet, enter a
                placeholder (e.g. TBD) and correct it later.
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() =>
                    setChequeModal({ open: false, record: null, value: "" })
                  }
                  className="px-4 py-2 text-sm font-medium text-gray-300 bg-[#1c2027] rounded-lg hover:bg-[#22262e]"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    savePaymentMethod(
                      chequeModal.record,
                      "cheque",
                      chequeModal.value,
                    )
                  }
                  disabled={!chequeModal.value.trim() || paymentSaving}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: ACCENT }}
                >
                  {paymentSaving ? "Saving…" : "Save cheque"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Picker modal ────────────────────────────────────────────── */}
        {pickerOpen && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div
              className="rounded-xl shadow-2xl max-w-6xl w-full h-[85vh] flex flex-col"
              style={{
                backgroundColor: "#161a20",
                border: "1px solid #2a2f37",
              }}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#22262e]">
                <div>
                  <h2 className="text-base font-semibold text-white">
                    {PICKER_TITLES[pickerKind]}
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Adding to:{" "}
                    <span className="text-gray-300 font-medium">
                      {addTargetWeek
                        ? targetWeekLabel(addTargetWeek)
                        : "Select a period"}
                    </span>
                    {pickerKind === "bill" && (
                      <span className="block text-amber-400/80 mt-0.5">
                        These bills will be marked paid when you complete this
                        pay period.
                      </span>
                    )}
                    {pickerKind === "agreement" && (
                      <span className="block text-amber-400/80 mt-0.5">
                        Completed agreements will be marked paid when you
                        complete this pay period.
                      </span>
                    )}
                    {pickerKind === "expense" && (
                      <span className="block text-amber-400/80 mt-0.5">
                        These expenses will be marked paid when you complete
                        this pay period.
                      </span>
                    )}
                    {pickerKind === "uniondues" && (
                      <div
                        className="flex items-center gap-1 rounded-lg p-0.5 mt-4"
                        style={{
                          backgroundColor: "#0e1014",
                          border: "1px solid #2a2f37",
                        }}
                      >
                        {["dollars", "hours"].map((m) => (
                          <button
                            key={m}
                            onClick={() => setDuesMethod(m)}
                            className="px-3 py-1.5 text-xs font-medium rounded-md transition-all"
                            style={{
                              backgroundColor:
                                duesMethod === m ? ACCENT : "transparent",
                              color: duesMethod === m ? "#fff" : "#9ca3af",
                            }}
                          >
                            {m === "dollars" ? "$ Dollars" : "⏱ Hours"}
                          </button>
                        ))}
                      </div>
                    )}
                  </p>
                </div>
                <button
                  onClick={closePicker}
                  className="text-gray-500 hover:text-gray-300 text-lg leading-none"
                >
                  ✕
                </button>
              </div>

              <div className="px-5 py-3 border-b border-[#22262e] flex flex-wrap items-center gap-2">
                <div className="min-w-[220px]">
                  {pickerLockedToPeriod ? (
                    <div className="text-sm text-gray-300 font-medium px-1 py-1.5">
                      {targetWeekLabel(addTargetWeek)}
                    </div>
                  ) : (
                    <Select
                      options={addTargetOptions}
                      value={
                        addTargetOptions.find(
                          (o) => o.value === addTargetWeek,
                        ) || null
                      }
                      onChange={(sel) => setAddTargetWeek(sel?.value || "")}
                      styles={customStyles}
                      placeholder="Pay period…"
                      isSearchable={false}
                    />
                  )}
                </div>
                {Object.keys(pickerColumnFilters).some((k) =>
                  isColumnFilterActive(k),
                ) && (
                  <button
                    onClick={clearAllPickerFilters}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-amber-400 bg-amber-900/20 border border-amber-700/40 hover:bg-amber-900/40 transition-colors"
                  >
                    ✕ Clear filters
                  </button>
                )}
                <span className="text-xs text-gray-500 ml-auto">
                  {pickerSelected.size} selected ·{" "}
                  {pickerKind === "uniondues"
                    ? `${sortedPickerItems.filter((i) => !isDuesRowLocked(i)).length} eligible`
                    : `${sortedPickerItems.length} available`}
                </span>
                <button
                  onClick={selectAllPicker}
                  className="px-2.5 py-1 text-xs text-gray-300 bg-[#1c2027] rounded-lg hover:bg-[#22262e]"
                >
                  Select all
                </button>
                <button
                  onClick={clearPickerSelection}
                  className="px-2.5 py-1 text-xs text-gray-400 hover:text-gray-200"
                >
                  Clear
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {pickerLoading ? (
                  <div className="py-12 text-center text-gray-500 text-sm">
                    Loading…
                  </div>
                ) : pickerItems.length === 0 ? (
                  <div className="py-12 text-center text-gray-500 text-sm">
                    Nothing available to add right now.
                  </div>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead
                      style={{ backgroundColor: "#14171d" }}
                      className="sticky top-0 z-10"
                    >
                      <tr>
                        <th className="px-4 py-2 w-8">
                          <span className="sr-only">Select</span>
                        </th>
                        {(PICKER_COLUMNS[pickerKind] || []).map((col) => {
                          const isOpen = openFilterColumn === col.key;
                          const active = isColumnFilterActive(col.key);
                          return (
                            <th
                              key={col.key}
                              className="px-3 py-2 text-left text-[11px] uppercase tracking-wider select-none"
                            >
                              <span className="inline-flex items-center gap-1.5">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    togglePickerSort(col.key);
                                  }}
                                  className={cls(
                                    "inline-flex items-center gap-1 hover:text-orange-300 transition-colors",
                                    active
                                      ? "text-orange-400"
                                      : "text-gray-500",
                                  )}
                                  title="Sort"
                                >
                                  {col.label}
                                  {pickerSort.key === col.key && (
                                    <span className="text-orange-400">
                                      {pickerSort.dir === "asc" ? "↑" : "↓"}
                                    </span>
                                  )}
                                </button>
                                <button
                                  data-picker-filter-dropdown
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openFilterDropdownAt(e, col.key, isOpen);
                                  }}
                                  title="Filter"
                                  className={cls(
                                    "inline-flex items-center justify-center w-6 h-6 rounded-md border transition-all",
                                    active
                                      ? "bg-orange-500 border-orange-400 text-white shadow-[0_0_0_1px_rgba(249,115,22,0.35)]"
                                      : "bg-[#1c2027] border-[#2a2f37] text-gray-400 hover:text-orange-300 hover:border-orange-500/50",
                                  )}
                                >
                                  <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path d="M3 4a1 1 0 011-1h12a1 1 0 01.8 1.6l-4.8 6.4V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-4L3.2 4.6A1 1 0 013 4z" />
                                  </svg>
                                </button>
                              </span>
                            </th>
                          );
                        })}

                        {pickerKind === "timesheet" && (
                          <>
                            <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-gray-500">
                              Clock In
                            </th>
                            <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-gray-500">
                              Clock Out
                            </th>
                            <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-gray-500">
                              Break
                            </th>
                            <SortableTh
                              label="Hours"
                              sortKey="hours"
                              active={pickerSort.key === "hours"}
                              dir={pickerSort.dir}
                              onSort={togglePickerSort}
                            />
                          </>
                        )}
                        {pickerKind === "piecework" && (
                          <SortableTh
                            label="Items"
                            sortKey="lineItemCount"
                            active={pickerSort.key === "lineItemCount"}
                            dir={pickerSort.dir}
                            onSort={togglePickerSort}
                          />
                        )}
                        {pickerKind === "salary" && (
                          <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-gray-500">
                            Hours this week
                          </th>
                        )}
                        {pickerKind === "expense" && (
                          <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-gray-500">
                            Reimburse To
                          </th>
                        )}

                        {pickerKind === "uniondues" ? (
                          <>
                            <SortableTh
                              label="Configured"
                              sortKey="configuredDuesAmount"
                              active={pickerSort.key === "configuredDuesAmount"}
                              dir={pickerSort.dir}
                              onSort={togglePickerSort}
                            />
                            <SortableTh
                              label="Deduction"
                              sortKey="deduction"
                              active={pickerSort.key === "deduction"}
                              dir={pickerSort.dir}
                              onSort={togglePickerSort}
                            />
                          </>
                        ) : (
                          <>
                            <SortableTh
                              label="Subtotal"
                              sortKey="subtotal"
                              active={pickerSort.key === "subtotal"}
                              dir={pickerSort.dir}
                              onSort={togglePickerSort}
                            />
                            {pickerKind !== "salary" && (
                              <SortableTh
                                label="Tax"
                                sortKey="tax"
                                active={pickerSort.key === "tax"}
                                dir={pickerSort.dir}
                                onSort={togglePickerSort}
                              />
                            )}
                            <SortableTh
                              label="Total"
                              sortKey="total"
                              active={pickerSort.key === "total"}
                              dir={pickerSort.dir}
                              onSort={togglePickerSort}
                            />
                          </>
                        )}
                      </tr>
                    </thead>

                    <tbody>
                      {sortedPickerItems.length === 0 && (
                        <tr>
                          <td
                            colSpan={20}
                            className="py-12 text-center text-gray-500 text-sm"
                          >
                            No items match the current filters.
                          </td>
                        </tr>
                      )}
                      {sortedPickerItems.map((item, idx) => {
                        const id = item._id || item.userId;
                        const checked = pickerSelected.has(id);
                        const locked =
                          pickerKind === "uniondues" && isDuesRowLocked(item);
                        return (
                          <tr
                            key={id}
                            onClick={() => !locked && togglePickerItem(id)}
                            className={cls(
                              "transition-colors",
                              locked
                                ? "cursor-not-allowed opacity-50"
                                : "cursor-pointer hover:bg-[#171b21]",
                            )}
                            style={{
                              borderTop:
                                idx === 0 ? "none" : "1px solid #1c2027",
                              backgroundColor: checked
                                ? "rgba(249,115,22,0.06)"
                                : "transparent",
                            }}
                          >
                            <td className="px-4 py-2.5">
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={locked}
                                onChange={() => !locked && togglePickerItem(id)}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 accent-orange-500 rounded bg-[#161a20] disabled:opacity-40"
                              />
                            </td>
                            <td className="px-3 py-2.5 text-gray-200">
                              {item.worker || (
                                <span className="text-amber-400/70 italic text-xs">
                                  Needs reimburse-to
                                </span>
                              )}
                              <span className="block text-[10px] text-gray-500">
                                {item.employeeNumber}
                              </span>
                            </td>

                            {pickerKind === "timesheet" && (
                              <>
                                <td className="px-3 py-2.5 text-gray-400 text-xs">
                                  {item.start
                                    ? new Date(item.start).toLocaleDateString()
                                    : "—"}
                                </td>
                                <td className="px-3 py-2.5 text-gray-400 text-xs">
                                  {item.project || "—"}
                                </td>
                                <td className="px-3 py-2.5 text-gray-400 text-xs">
                                  {item.woNumber || "—"}
                                </td>
                                <td className="px-3 py-2.5 text-gray-400 text-xs font-mono">
                                  {clockTime(item.start)}
                                </td>
                                <td className="px-3 py-2.5 text-gray-400 text-xs font-mono">
                                  {clockTime(item.end)}
                                </td>
                                <td className="px-3 py-2.5 text-right text-gray-400 text-xs font-mono">
                                  {formatBreakDuration(breakMinutesTotal(item))}
                                </td>
                                <td className="px-3 py-2.5 text-right font-mono text-gray-300">
                                  {item.hours != null
                                    ? item.hours.toFixed(2)
                                    : "—"}
                                </td>
                              </>
                            )}

                            {pickerKind === "piecework" && (
                              <>
                                <td className="px-3 py-2.5 text-gray-400 text-xs font-mono">
                                  {item.logNumber}
                                </td>
                                <td className="px-3 py-2.5 text-gray-400 text-xs">
                                  {item.project || "—"}
                                </td>
                                <td className="px-3 py-2.5 text-gray-400 text-xs">
                                  {item.woNumber || "—"}
                                </td>
                                <td className="px-3 py-2.5 text-gray-400 text-xs">
                                  {item.workCompletedDate
                                    ? new Date(
                                        item.workCompletedDate,
                                      ).toLocaleDateString()
                                    : "—"}
                                </td>
                                <td className="px-3 py-2.5 text-gray-400 text-xs">
                                  {item.approvedAt
                                    ? new Date(
                                        item.approvedAt,
                                      ).toLocaleDateString()
                                    : "—"}
                                </td>
                                <td className="px-3 py-2.5 text-right text-gray-400 text-xs">
                                  {item.lineItemCount ?? 0}
                                </td>
                              </>
                            )}

                            {(pickerKind === "bill" ||
                              pickerKind === "agreement") && (
                              <>
                                <td className="px-3 py-2.5 text-gray-400 text-xs font-mono">
                                  {item.invoiceNumber}
                                </td>
                                <td className="px-3 py-2.5 text-gray-400 text-xs">
                                  {item.project || "—"}
                                </td>
                              </>
                            )}

                            {pickerKind === "uniondues" && (
                              <td className="px-3 py-2.5 text-gray-400 text-xs">
                                {item.union || "—"}
                              </td>
                            )}

                            {pickerKind === "expense" && (
                              <>
                                <td className="px-3 py-2.5 text-gray-400 text-xs font-mono">
                                  {item.invoiceNumber}
                                </td>
                                <td className="px-3 py-2.5 text-gray-400 text-xs">
                                  {item.project || "—"}
                                </td>
                                <td className="px-3 py-2.5 text-gray-400 text-xs">
                                  {item.moNumber != null
                                    ? String(item.moNumber)
                                    : "—"}
                                </td>
                                <td
                                  className="px-3 py-2.5"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <select
                                    value={
                                      reimburseToOverrides[item._id] ??
                                      item.userId ??
                                      ""
                                    }
                                    onChange={(e) =>
                                      setReimburseToOverrides((p) => ({
                                        ...p,
                                        [item._id]: e.target.value,
                                      }))
                                    }
                                    className="w-40 text-xs px-2 py-1 bg-[#0e1014] border border-[#2a2f37] rounded text-gray-200 focus:border-orange-500 focus:outline-none"
                                  >
                                    <option value="">Select worker…</option>
                                    {reimbursableUsers.map((u) => (
                                      <option key={u._id} value={u._id}>
                                        {u.displayName}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                              </>
                            )}

                            {pickerKind === "salary" && (
                              <td
                                className="px-3 py-2.5 text-right"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="number"
                                  step="0.25"
                                  value={
                                    salaryHourOverrides[id] ?? item.hoursPerWeek
                                  }
                                  onChange={(e) =>
                                    setSalaryHourOverrides((p) => ({
                                      ...p,
                                      [id]: e.target.value,
                                    }))
                                  }
                                  className="w-20 text-right px-2 py-1 text-xs bg-[#0e1014] border border-[#2a2f37] rounded text-gray-200 focus:border-orange-500 focus:outline-none font-mono"
                                />
                              </td>
                            )}

                            {pickerKind === "uniondues" ? (
                              <>
                                <td className="px-3 py-2.5 text-right font-mono text-gray-400">
                                  {item.configuredDuesAmount
                                    ? money(item.configuredDuesAmount)
                                    : "—"}
                                </td>
                                {(() => {
                                  if (item.alreadyCharged) {
                                    return (
                                      <td className="px-3 py-2.5 text-right">
                                        <span className="inline-flex flex-col items-end">
                                          <span className="text-[11px] font-medium text-emerald-400">
                                            ✓ Charged{" "}
                                            {money(item.chargedAmount)}
                                          </span>
                                          {item.chargedHours > 0 && (
                                            <span className="text-[10px] text-gray-500">
                                              {item.chargedHours.toFixed(2)}h
                                            </span>
                                          )}
                                          {item.chargedAt && (
                                            <span className="text-[10px] text-gray-600">
                                              {new Date(
                                                item.chargedAt,
                                              ).toLocaleDateString()}
                                            </span>
                                          )}
                                        </span>
                                      </td>
                                    );
                                  }
                                  if (!(item.configuredDuesAmount > 0)) {
                                    return (
                                      <td className="px-3 py-2.5 text-right text-xs text-gray-600">
                                        No dues configured
                                      </td>
                                    );
                                  }
                                  const preview = previewDuesDeduction(
                                    item,
                                    duesMethod,
                                  );
                                  if (preview.blocked) {
                                    return (
                                      <td className="px-3 py-2.5 text-right text-xs text-amber-400/80">
                                        No pay rate
                                      </td>
                                    );
                                  }
                                  return (
                                    <td className="px-3 py-2.5 text-right font-mono font-semibold text-red-400">
                                      − {money(preview.amountDeducted)}
                                      {duesMethod === "hours" && (
                                        <span className="block text-[10px] text-gray-500 font-normal">
                                          {preview.hoursDeducted.toFixed(2)}h
                                        </span>
                                      )}
                                    </td>
                                  );
                                })()}
                              </>
                            ) : (
                              <>
                                <td className="px-3 py-2.5 text-right font-mono text-gray-300">
                                  {money(item.subtotal)}
                                </td>
                                {pickerKind !== "salary" && (
                                  <td className="px-3 py-2.5 text-right font-mono text-gray-500">
                                    {item.tax > 0 ? money(item.tax) : "—"}
                                  </td>
                                )}
                                <td className="px-3 py-2.5 text-right font-mono font-semibold text-orange-300">
                                  {money(item.total)}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              {openFilterColumn &&
                filterDropdownPos &&
                (() => {
                  const col = (PICKER_COLUMNS[pickerKind] || []).find(
                    (c) => c.key === openFilterColumn,
                  );
                  if (!col) return null;
                  const options = pickerColumnOptions(col.key);
                  const currentFilter = pickerColumnFilters[col.key];
                  const selectedSet =
                    currentFilter instanceof Set ? currentFilter : new Set();

                  return (
                    <div
                      data-picker-filter-dropdown
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: "fixed",
                        top: filterDropdownPos.top,
                        left: filterDropdownPos.left,
                        zIndex: 200,
                      }}
                      className="bg-[#161a20] border border-[#2a2f37] rounded-lg shadow-2xl w-64 max-h-80 overflow-hidden flex flex-col"
                    >
                      <div className="flex items-center justify-between px-3 py-2 border-b border-[#22262e]">
                        <span className="text-[10px] uppercase tracking-wider text-gray-500">
                          {col.label}
                        </span>
                        <div className="flex items-center gap-3">
                          {selectedSet.size > 0 && (
                            <span className="text-[10px] text-orange-400">
                              {selectedSet.size} selected
                            </span>
                          )}
                          {isColumnFilterActive(col.key) && (
                            <button
                              onClick={() => clearColumnFilter(col.key)}
                              className="text-[11px] text-orange-400 hover:text-orange-300"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                      {options.length > 8 && (
                        <div className="px-2 pt-2">
                          <input
                            autoFocus
                            type="text"
                            placeholder={`Search ${col.label.toLowerCase()}…`}
                            value={
                              pickerColumnFilters[`${col.key}__search`] || ""
                            }
                            onChange={(e) =>
                              setPickerColumnFilters((prev) => ({
                                ...prev,
                                [`${col.key}__search`]: e.target.value,
                              }))
                            }
                            className="w-full rounded bg-[#0e1014] border border-[#2a2f37] text-gray-200 text-xs py-1.5 px-2 mb-1 focus:outline-none focus:border-orange-500"
                          />
                        </div>
                      )}
                      <div className="overflow-y-auto py-1 flex-1">
                        {options
                          .filter((val) => {
                            const q = (
                              pickerColumnFilters[`${col.key}__search`] || ""
                            ).toLowerCase();
                            return !q || val.toLowerCase().includes(q);
                          })
                          .map((val) => {
                            const selected = selectedSet.has(val);
                            return (
                              <label
                                key={val}
                                className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-300 hover:bg-[#1c2027] cursor-pointer normal-case font-normal"
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() =>
                                    toggleChoiceFilterValue(col.key, val)
                                  }
                                  className="h-3.5 w-3.5 accent-orange-500 rounded bg-[#0e1014]"
                                />
                                <span className="truncate">{val}</span>
                              </label>
                            );
                          })}
                        {options.length === 0 && (
                          <div className="px-3 py-4 text-xs text-gray-600 text-center">
                            No values
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              <div className="flex items-center justify-between px-5 py-4 border-t border-[#22262e]">
                <span className="text-xs text-gray-500">
                  {pickerSelected.size > 0 &&
                    (() => {
                      const chosen = sortedPickerItems.filter((i) =>
                        pickerSelected.has(i._id || i.userId),
                      );

                      if (pickerKind === "uniondues") {
                        const totalDeducted = chosen.reduce((s, i) => {
                          const p = previewDuesDeduction(i, duesMethod);
                          return s + (p.blocked ? 0 : p.amountDeducted);
                        }, 0);
                        return `${chosen.length} worker${chosen.length === 1 ? "" : "s"} · ${money(totalDeducted)} total, deducted as ${duesMethod}`;
                      }

                      const subtotalSum = chosen.reduce(
                        (s, i) => s + (i.subtotal || 0),
                        0,
                      );
                      const taxSum = chosen.reduce(
                        (s, i) => s + (i.tax || 0),
                        0,
                      );
                      const totalSum = chosen.reduce(
                        (s, i) => s + (i.total ?? i.amount ?? 0),
                        0,
                      );
                      return taxSum > 0
                        ? `Subtotal: ${money(subtotalSum)} · Tax: ${money(taxSum)} · Total: ${money(totalSum)}`
                        : `Total: ${money(totalSum)}`;
                    })()}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={closePicker}
                    className="px-4 py-2 text-sm font-medium text-gray-300 bg-[#1c2027] rounded-lg hover:bg-[#22262e]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitPicker}
                    disabled={pickerSelected.size === 0 || pickerSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
                    style={{ backgroundColor: ACCENT }}
                  >
                    {pickerSubmitting
                      ? pickerKind === "uniondues"
                        ? "Charging…"
                        : "Adding…"
                      : pickerKind === "uniondues"
                        ? `Charge Dues (${pickerSelected.size || 0})`
                        : `Add ${pickerSelected.size || ""}`.trim()}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
          <div>
            <p
              className="text-[11px] uppercase tracking-[0.14em] font-medium mb-1"
              style={{ color: ACCENT }}
            >
              Payroll
            </p>
            <h1 className="text-2xl font-semibold text-white tracking-tight">
              {viewMode === "periods" ? "Pay Periods" : "Period Report"}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDuesHistoryOpen(true)}
              className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150 hover:brightness-110 active:scale-[0.98]"
              style={{
                color: ACCENT,
                backgroundColor: `${ACCENT}14`,
                border: `1px solid ${ACCENT}40`,
              }}
            >
              Union Dues History
            </button>
            <button
              onClick={() => openExportModal()}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-150 shadow-sm hover:brightness-110 active:scale-[0.98]"
              style={{
                backgroundColor: "#1c2027",
                border: "1px solid #2a2f37",
              }}
            >
              Export
            </button>
          </div>
        </div>

        {isExportModalOpen && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div
              className="p-6 rounded-xl shadow-2xl max-w-md w-full"
              style={{
                backgroundColor: "#161a20",
                border: "1px solid #2a2f37",
              }}
            >
              <h2 className="text-base font-semibold text-white mb-4">
                Export Options
              </h2>
              {exportStep === 1 && (
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-2">
                    Select Export Type
                  </label>
                  <Select
                    options={EXPORT_OPTIONS}
                    value={
                      EXPORT_OPTIONS.find((o) => o.value === exportType) || null
                    }
                    onChange={(selected) =>
                      setExportType(selected ? selected.value : "")
                    }
                    className="text-sm mb-4"
                    styles={customStyles}
                  />
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={closeExportModal}
                      className="px-4 py-2 text-sm font-medium text-gray-300 bg-[#1c2027] rounded-lg hover:bg-[#22262e] transition-colors duration-150"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleNextStep}
                      className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:brightness-110 transition-all duration-150"
                      style={{ backgroundColor: ACCENT }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
              {exportStep === 2 &&
                [
                  "csv",
                  "hourly",
                  "ripplingHourly",
                  "ripplingSalary",
                  "hourlyWorkers",
                ].includes(exportType) && (
                  <div>
                    <p className="text-xs text-gray-500 mb-3">
                      {
                        {
                          hourly:
                            "Exporting hourly + salary hours for the selected range.",
                          csv: "Exporting piecework + subcontractor earnings for the selected range.",
                          ripplingHourly:
                            "Exporting hourly + piecework (with HST, expenses and union dues) for Rippling.",
                          ripplingSalary:
                            "Exporting salary hours and amounts for Rippling.",
                          hourlyWorkers:
                            "Exporting hours and hourly rate for hourly-type site workers (separate payroll upload).",
                        }[exportType]
                      }
                    </p>

                    <label className="block text-xs font-medium text-gray-400 mb-2">
                      Range
                    </label>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {[
                        { value: "week", label: "1 Week" },
                        { value: "2weeks", label: "2 Weeks" },
                        { value: "month", label: "Month" },
                        { value: "custom", label: "Custom" },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setExportRangeMode(opt.value)}
                          className="px-3 py-2 text-sm font-medium rounded-lg transition-all duration-150"
                          style={{
                            backgroundColor:
                              exportRangeMode === opt.value
                                ? ACCENT
                                : "#1c2027",
                            color:
                              exportRangeMode === opt.value
                                ? "#fff"
                                : "#d1d5db",
                            border: "1px solid #2a2f37",
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {exportRangeMode !== "custom" ? (
                      <>
                        <label className="block text-xs font-medium text-gray-400 mb-2">
                          {exportRangeMode === "month"
                            ? "Any week in the month"
                            : "Starting week"}
                        </label>
                        <Select
                          options={weekPeriodOptions}
                          value={
                            weekPeriodOptions.find(
                              (option) => option.value === exportAnchorWeek,
                            ) || null
                          }
                          onChange={(selected) =>
                            setExportAnchorWeek(selected ? selected.value : "")
                          }
                          className="text-sm mb-2"
                          styles={customStyles}
                        />
                        {exportStartWeek && exportEndWeek && (
                          <p className="text-xs text-gray-500 mb-4">
                            {formatDate(exportStartWeek)} –{" "}
                            {formatDate(exportEndWeek)}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <label className="block text-xs font-medium text-gray-400 mb-2">
                          Export Start Week
                        </label>
                        <Select
                          options={weekPeriodOptions}
                          value={
                            weekPeriodOptions.find(
                              (option) => option.value === exportStartWeek,
                            ) || null
                          }
                          onChange={(selected) =>
                            setExportStartWeek(selected ? selected.value : "")
                          }
                          className="text-sm mb-4"
                          styles={customStyles}
                        />
                        <label className="block text-xs font-medium text-gray-400 mb-2">
                          Export End Week
                        </label>
                        <Select
                          options={weekPeriodOptions}
                          value={
                            weekPeriodOptions.find(
                              (option) => option.value === exportEndWeek,
                            ) || null
                          }
                          onChange={(selected) =>
                            setExportEndWeek(selected ? selected.value : "")
                          }
                          className="text-sm mb-4"
                          styles={customStyles}
                        />
                      </>
                    )}

                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setExportStep(1)}
                        className="px-4 py-2 text-sm font-medium text-gray-300 bg-[#1c2027] rounded-lg hover:bg-[#22262e] transition-colors duration-150"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleExport}
                        className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:brightness-110 transition-all duration-150"
                        style={{ backgroundColor: ACCENT }}
                      >
                        Export
                      </button>
                    </div>
                  </div>
                )}
              {exportStep === 2 && exportType === "unionDues" && (
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-2">
                    Union Dues Month
                  </label>
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="w-full p-2 rounded-lg bg-[#161a20] border border-[#2a2f37] text-gray-200 focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm outline-none"
                  />
                  <label className="block text-xs font-medium text-gray-400 mt-4 mb-2">
                    Filter by Union (optional)
                  </label>
                  <Select
                    options={unionOptions}
                    value={
                      unionOptions.find(
                        (option) => option.value === selectedUnion,
                      ) || null
                    }
                    onChange={(selected) =>
                      setSelectedUnion(selected ? selected.value : "all")
                    }
                    className="text-sm mb-4"
                    styles={customStyles}
                  />
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => setDuesHistoryOpen(true)}
                      className="mr-auto px-3 py-2 text-sm text-orange-400 hover:text-orange-300"
                    >
                      Remittance history
                    </button>
                    <button
                      onClick={() => setExportStep(1)}
                      className="px-4 py-2 text-sm font-medium text-gray-300 bg-[#1c2027] rounded-lg hover:bg-[#22262e] transition-colors duration-150"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleExport}
                      className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:brightness-110 transition-all duration-150"
                      style={{ backgroundColor: ACCENT }}
                    >
                      Next: Select Entries
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <UnionDuesHistoryModal
          open={duesHistoryOpen}
          apiBase={API}
          getHeaders={headers}
          onClose={() => setDuesHistoryOpen(false)}
          onNotify={(type, title, message) =>
            setAlert({ open: true, type, title, message })
          }
        />

        <UnionDuesExportModal
          open={duesExportOpen}
          month={selectedMonth}
          initialUnion={selectedUnion}
          apiBase={API}
          getHeaders={headers}
          onClose={() => {
            setDuesExportOpen(false);
            setSelectedUnion("all");
            setExportType("");
          }}
          onDone={(type, title, message) =>
            setAlert({
              open: true,
              type,
              title,
              message,
              onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
            })
          }
        />

        {viewMode === "periods" && (
          <>
            <div className="mb-5 flex items-center flex-wrap gap-3">
              <label
                htmlFor="filterDate"
                className="text-xs font-medium text-gray-400"
              >
                Jump to date
              </label>
              <input
                type="date"
                id="filterDate"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="p-2 rounded-lg bg-[#161a20] border border-[#2a2f37] text-gray-200 focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm outline-none"
              />
              <button
                onClick={() => setFilterDate("")}
                className="px-3 py-2 text-xs font-medium text-gray-300 bg-[#1c2027] rounded-lg hover:bg-[#22262e] transition-colors duration-150"
              >
                Clear
              </button>
            </div>

            <div className="mb-2">
              <div
                className="mb-3 flex items-center flex-wrap gap-3 rounded-lg px-4 py-3 transition-colors"
                style={{
                  backgroundColor:
                    selectedPeriods.size > 0
                      ? "rgba(249,115,22,0.07)"
                      : "#14171d",
                  border: `1px solid ${selectedPeriods.size > 0 ? `${ACCENT}66` : "#22262e"}`,
                }}
              >
                <p className="text-sm text-gray-300">
                  {selectedPeriods.size === 0
                    ? "Tick one or more weeks to view them together as a combined report."
                    : `${selectedPeriods.size} week${selectedPeriods.size === 1 ? "" : "s"} selected`}
                </p>
                <div className="ml-auto flex items-center gap-2">
                  {selectedPeriods.size > 0 && (
                    <button
                      onClick={() => setSelectedPeriods(new Set())}
                      className="px-3 py-2 text-xs font-medium text-gray-300 bg-[#1c2027] rounded-lg hover:bg-[#22262e]"
                    >
                      Clear selection
                    </button>
                  )}
                  <button
                    onClick={handleViewSelectedPeriods}
                    disabled={selectedPeriods.size === 0}
                    className="px-4 py-2 text-sm font-semibold text-white rounded-lg hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    style={{ backgroundColor: ACCENT }}
                  >
                    {selectedPeriods.size > 0
                      ? `View ${selectedPeriods.size} selected period${selectedPeriods.size === 1 ? "" : "s"} →`
                      : "View selected periods →"}
                  </button>
                </div>
              </div>
              <div
                className="rounded-xl overflow-hidden"
                style={{ border: "1px solid #22262e" }}
              >
                <table className="min-w-full">
                  <thead style={{ backgroundColor: "#161a20" }}>
                    <tr>
                      <th className="px-4 py-3 text-left w-10">
                        <input
                          type="checkbox"
                          checked={allWeeksSelected}
                          onChange={toggleAllWeeks}
                          title="Select / deselect all weeks shown"
                          className="h-4 w-4 accent-orange-500 rounded bg-[#161a20] border-gray-600"
                        />
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                        Week Start
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                        Week End
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekPeriods.map((week, i) => {
                      const status = periodStatusByWeek[week.value];
                      const locked = status?.locked || false;
                      const hasEntries = status?.hasEntries || false;
                      return (
                        <tr
                          key={week.value}
                          className="transition-colors duration-150 hover:bg-[#171b21]"
                          style={{
                            backgroundColor: week.isCurrentMonth
                              ? "#14171d"
                              : "transparent",
                            borderTop: i === 0 ? "none" : "1px solid #1c2027",
                          }}
                        >
                          <td className="px-4 py-3.5 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedPeriods.has(week.value)}
                              onChange={() => togglePeriodSelection(week.value)}
                              className="h-4 w-4 accent-orange-500 rounded bg-[#161a20] border-gray-600"
                            />
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-200 font-mono">
                            {formatDate(week.start)}
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-200 font-mono">
                            {formatDate(week.end)}
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-sm">
                            <span className="inline-flex items-center gap-2">
                              <StatusPill
                                status={
                                  locked
                                    ? "processed"
                                    : hasEntries
                                      ? "pending"
                                      : "pending"
                                }
                              />
                              {hasEntries && (
                                <span className="text-[11px] text-gray-500">
                                  {status.lineCount} item
                                  {status.lineCount === 1 ? "" : "s"}
                                </span>
                              )}
                              {!hasEntries && (
                                <span className="text-[11px] text-gray-600">
                                  Empty
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleSelectPeriod(week.value)}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg border transition-all hover:brightness-125"
                              style={{
                                color: ACCENT,
                                borderColor: `${ACCENT}55`,
                                backgroundColor: `${ACCENT}14`,
                              }}
                            >
                              {hasEntries ? "View / Edit →" : "Open →"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-5 flex flex-col sm:flex-row justify-between items-center gap-3">
                <button
                  onClick={handleLoadMore}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-[#1c2027] rounded-lg hover:bg-[#22262e] transition-colors duration-150"
                >
                  Load more weeks
                </button>
              </div>
            </div>
          </>
        )}

        {viewMode === "report" && period && (
          <>
            <div className="mb-5 flex justify-between items-center flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-400">
                  {period.count
                    ? `Combined view of ${period.count} period${period.count === 1 ? "" : "s"}: ${formatDate(period.start)} – ${formatDate(period.end)}`
                    : `Pay period: ${formatDate(period.start)} – ${formatDate(period.end)}`}
                </p>
                {!period.count && (
                  <span
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium"
                    style={{
                      backgroundColor: periodLocked
                        ? "rgba(52,211,153,0.12)"
                        : "rgba(250,204,21,0.12)",
                      color: periodLocked ? "#34d399" : "#facc15",
                    }}
                  >
                    <LockIcon locked={periodLocked} />
                    {periodLocked ? "Locked" : "Draft"}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!period.count && !periodLocked && (
                  <>
                    <button
                      onClick={() => {
                        setAddTargetWeek(selectedMonday);
                        setPickerLockedToPeriod(true);
                        openPicker("timesheet");
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-white rounded-lg hover:brightness-110 transition-all"
                      style={{ backgroundColor: "#3b82f6" }}
                    >
                      + Timesheets
                    </button>
                    <button
                      onClick={() => {
                        setAddTargetWeek(selectedMonday);
                        setPickerLockedToPeriod(true);
                        openPicker("piecework");
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-white rounded-lg hover:brightness-110 transition-all"
                      style={{ backgroundColor: ACCENT }}
                    >
                      + Piecework
                    </button>
                    <button
                      onClick={() => {
                        setAddTargetWeek(selectedMonday);
                        setPickerLockedToPeriod(true);
                        openPicker("bill");
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-white rounded-lg hover:brightness-110 transition-all"
                      style={{ backgroundColor: "#34d399" }}
                    >
                      + Subcontractor
                    </button>
                    <button
                      onClick={() => {
                        setAddTargetWeek(selectedMonday);
                        setPickerLockedToPeriod(true);
                        openPicker("agreement");
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-white rounded-lg hover:brightness-110 transition-all"
                      style={{ backgroundColor: "#10b981" }}
                    >
                      + Sub Agreements
                    </button>
                    <button
                      onClick={() => {
                        setAddTargetWeek(selectedMonday);
                        setPickerLockedToPeriod(true);
                        openPicker("salary", selectedMonday);
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-white rounded-lg hover:brightness-110 transition-all"
                      style={{ backgroundColor: "#a78bfa" }}
                    >
                      + Salary
                    </button>
                    <button
                      onClick={() => {
                        setAddTargetWeek(selectedMonday);
                        setPickerLockedToPeriod(true);
                        openPicker("expense");
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-white rounded-lg hover:brightness-110 transition-all"
                      style={{ backgroundColor: "#f472b6" }}
                    >
                      + Expenses
                    </button>
                    <button
                      onClick={() => {
                        setAddTargetWeek(selectedMonday);
                        setPickerLockedToPeriod(true);
                        openUnionDuesPicker(selectedMonday);
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-white rounded-lg hover:brightness-110 transition-all"
                      style={{ backgroundColor: "#ef4444" }}
                    >
                      + Union Dues
                    </button>
                    <button
                      onClick={() => setLockConfirm(true)}
                      disabled={payrollReport.length === 0}
                      className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      style={{ backgroundColor: "#16a34a" }}
                    >
                      ✓ Complete Period
                    </button>
                  </>
                )}
                {!period.count && periodLocked && (
                  <button
                    onClick={() => setUnlockConfirm(true)}
                    className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg hover:brightness-110 transition-all"
                    style={{ backgroundColor: "#e85d24" }}
                  >
                    🔓 Unlock Period
                  </button>
                )}
                <button
                  onClick={handleBack}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-[#1c2027] rounded-lg hover:bg-[#22262e] transition-colors duration-150"
                >
                  ← Back to periods
                </button>
              </div>
            </div>

            {!period.count && !periodLocked && payrollReport.length === 0 && (
              <div
                className="mb-5 rounded-lg p-5 text-center"
                style={{ border: "1px dashed #2a2f37" }}
              >
                <p className="text-sm text-gray-400">
                  Nothing added to this period yet. Use the buttons above to add
                  approved timesheets, piecework logs, subcontractor bills, or
                  salary workers.
                </p>
              </div>
            )}

            {/* ── Summary strip ──────────────────────────────────── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {[
                {
                  label: "Workers",
                  value: reportTotals.workers.size,
                  mono: false,
                },
                {
                  label: "Hours",
                  value: reportTotals.hours.toFixed(2),
                  mono: true,
                },
                {
                  label: "Gross",
                  value: money(reportTotals.amount),
                  mono: true,
                },
                {
                  label: "Total pay",
                  value: money(reportTotals.totalPay),
                  mono: true,
                  accent: true,
                },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-xl p-4"
                  style={{
                    backgroundColor: "#14171d",
                    border: "1px solid #22262e",
                  }}
                >
                  <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">
                    {stat.label}
                  </p>
                  <p
                    className={`text-xl font-semibold ${stat.mono ? "font-mono" : ""}`}
                    style={{ color: stat.accent ? ACCENT : "#fff" }}
                  >
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>

            {Object.keys(reportColumnFilters).some((k) =>
              isReportColumnFilterActive(k),
            ) && (
              <button
                onClick={clearAllReportFilters}
                className="mb-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-amber-400 bg-amber-900/20 border border-amber-700/40 hover:bg-amber-900/40 transition-colors"
              >
                ✕ Clear filters (
                {
                  Object.keys(reportColumnFilters).filter((k) =>
                    isReportColumnFilterActive(k),
                  ).length
                }
                )
              </button>
            )}
            <div
              className="rounded-xl overflow-x-auto"
              style={{ border: "1px solid #22262e" }}
            >
              <table className="min-w-full">
                <thead style={{ backgroundColor: "#161a20" }}>
                  <tr>
                    {REPORT_COLUMNS.map((col) => (
                      <FilterableTh
                        key={col.key}
                        col={col}
                        activeSort={reportSort.key === col.key}
                        sortDir={reportSort.dir}
                        onSort={toggleReportSort}
                        isOpen={openReportFilterColumn === col.key}
                        onToggleOpen={(e, k) =>
                          openReportFilterDropdownAt(
                            e,
                            k,
                            openReportFilterColumn === k,
                          )
                        }
                        isFilterActive={isReportColumnFilterActive(col.key)}
                      />
                    ))}
                    <SortableTh
                      label="Hours"
                      sortKey="hoursWorked"
                      active={reportSort.key === "hoursWorked"}
                      dir={reportSort.dir}
                      onSort={toggleReportSort}
                    />
                    <SortableTh
                      label="Rate"
                      sortKey="payRate"
                      active={reportSort.key === "payRate"}
                      dir={reportSort.dir}
                      onSort={toggleReportSort}
                    />
                    <SortableTh
                      label="Subtotal"
                      sortKey="amount"
                      active={reportSort.key === "amount"}
                      dir={reportSort.dir}
                      onSort={toggleReportSort}
                    />
                    <SortableTh
                      label="Tax"
                      sortKey="tax"
                      active={reportSort.key === "tax"}
                      dir={reportSort.dir}
                      onSort={toggleReportSort}
                    />
                    <SortableTh
                      label="Total Pay"
                      sortKey="totalPay"
                      active={reportSort.key === "totalPay"}
                      dir={reportSort.dir}
                      onSort={toggleReportSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {payrollReport.length > 0 ? (
                    sortedPayrollReport.map((record, idx) => {
                      const userId = record._id || record.email;
                      const payType = record.payType?.toLowerCase() || "";
                      const rowKey = `${userId}_${payType}`;
                      const isExpanded = expandedRow === rowKey;
                      const isLoading = breakdownLoading[rowKey];
                      const bd = breakdownData[rowKey];
                      const rowLines = lineItems.filter(
                        (li) =>
                          String(li.userId) === String(userId) &&
                          li.payType?.toLowerCase() === payType,
                      );

                      return (
                        <React.Fragment key={rowKey || idx}>
                          <tr
                            className="cursor-pointer transition-colors duration-150"
                            style={{
                              backgroundColor: isExpanded
                                ? "#171b21"
                                : "transparent",
                              borderTop:
                                idx === 0 ? "none" : "1px solid #1c2027",
                            }}
                            onMouseEnter={(e) => {
                              if (!isExpanded)
                                e.currentTarget.style.backgroundColor =
                                  "#14171d";
                            }}
                            onMouseLeave={(e) => {
                              if (!isExpanded)
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                            }}
                            onClick={() => toggleRowExpansion(record)}
                          >
                            <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-300 font-mono">
                              {record.employeeNumber || "N/A"}
                            </td>
                            <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-100 font-medium">
                              {record.displayName || "Unknown"}
                            </td>
                            <td className="px-5 py-3.5 whitespace-nowrap text-sm">
                              <PayTypeTag payType={record.payType} />
                            </td>
                            <td className="px-5 py-3.5 whitespace-nowrap text-sm">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border"
                                style={
                                  isHourlyWorkerRow(record)
                                    ? {
                                        color: "#60a5fa",
                                        borderColor: "#60a5fa40",
                                        backgroundColor: "#60a5fa14",
                                      }
                                    : {
                                        color: "#9ca3af",
                                        borderColor: "#9ca3af40",
                                        backgroundColor: "#9ca3af14",
                                      }
                                }
                              >
                                {payrollGroupOf(record)}
                              </span>
                            </td>
                            <td
                              className="px-5 py-3.5 whitespace-nowrap text-sm"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {payType === "uniondues" || period.count ? (
                                <span className="text-gray-600 text-xs">—</span>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <select
                                    value={
                                      record.paymentMethod || "direct_deposit"
                                    }
                                    onChange={(e) =>
                                      handlePaymentMethodChange(
                                        record,
                                        e.target.value,
                                      )
                                    }
                                    className="text-xs px-2 py-1 bg-[#0e1014] border border-[#2a2f37] rounded text-gray-200 focus:border-orange-500 focus:outline-none"
                                  >
                                    <option value="direct_deposit">
                                      Direct Deposit
                                    </option>
                                    <option value="cheque">Cheque</option>
                                  </select>
                                  {record.paymentMethod === "cheque" && (
                                    <button
                                      onClick={() => openChequeModal(record)}
                                      title="Edit cheque number"
                                      className="font-mono text-xs px-2 py-1 rounded border transition-colors hover:brightness-125"
                                      style={{
                                        color: ACCENT,
                                        borderColor: `${ACCENT}40`,
                                        backgroundColor: `${ACCENT}14`,
                                      }}
                                    >
                                      #{record.chequeNumber || "—"}
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-300 text-right font-mono">
                              {(record.hoursWorked ?? 0).toFixed(2)}
                            </td>
                            <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-300 text-right font-mono">
                              {money(record.payRate || 0)}
                            </td>
                            <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-300 text-right font-mono">
                              {money(record.amount)}
                            </td>
                            <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-300 text-right font-mono">
                              {money(record.tax)}
                            </td>
                            <td className="px-5 py-3.5 whitespace-nowrap text-sm text-right font-mono font-semibold text-white">
                              {money(record.totalPay)}
                            </td>
                            <td className="px-5 py-3.5 whitespace-nowrap text-sm">
                              <StatusPill status={record.status} />
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr key={`${rowKey}-breakdown`}>
                              <td colSpan="11" className="p-0">
                                <div
                                  className="p-6"
                                  style={{
                                    backgroundColor: "#0e1014",
                                    borderTop: "1px solid #1c2027",
                                    borderBottom: "1px solid #1c2027",
                                  }}
                                >
                                  {/* Editable line list — only when the period is a
                                      draft. Locked periods show read-only breakdown.
                                      Detail (log #, WO, dates) isn't on the line item
                                      itself — GET /payroll only returns the aggregate
                                      fields (amount/tax/totalPay/sourceModel/sourceId).
                                      Once `bd` (POST /payroll/breakdown) has loaded we
                                      join each line to its rich detail by lineId so
                                      "Piecework log" etc. isn't the only text shown. */}
                                  {!period.count &&
                                    !periodLocked &&
                                    rowLines.length > 0 &&
                                    (() => {
                                      const pieceworkByLine = new Map(
                                        (bd?.pieceworker || []).map((p) => [
                                          String(p.lineId),
                                          p,
                                        ]),
                                      );
                                      const hourlyByLine = new Map(
                                        (bd?.hourly || []).map((h) => [
                                          String(h.lineId),
                                          h,
                                        ]),
                                      );
                                      const subByLine = new Map(
                                        (bd?.subcontractor || []).map((s) => [
                                          String(s.lineId),
                                          s,
                                        ]),
                                      );

                                      return (
                                        <div className="mb-6">
                                          <h4 className="text-[13px] font-semibold text-gray-300 mb-3">
                                            Line items ({rowLines.length})
                                          </h4>
                                          <div
                                            className="rounded-lg overflow-hidden"
                                            style={{
                                              border: "1px solid #1c2027",
                                            }}
                                          >
                                            {rowLines.map((li, i) => {
                                              const pw = pieceworkByLine.get(
                                                String(li._id),
                                              );
                                              const ts = hourlyByLine.get(
                                                String(li._id),
                                              );
                                              const bill = subByLine.get(
                                                String(li._id),
                                              );

                                              let label;
                                              let detail = null;

                                              if (
                                                li.sourceModel === "Timesheet"
                                              ) {
                                                label = `Timesheet · ${(li.totalHours || 0).toFixed(2)}h`;
                                                if (ts) {
                                                  detail = [
                                                    ts.start
                                                      ? format(
                                                          new Date(ts.start),
                                                          "MMM d, yyyy",
                                                        )
                                                      : null,
                                                    ts.workOrder?.woNumber
                                                      ? `WO ${ts.workOrder.woNumber}`
                                                      : null,
                                                  ]
                                                    .filter(Boolean)
                                                    .join(" · ");
                                                }
                                              } else if (
                                                li.sourceModel ===
                                                "PieceworkerLog3"
                                              ) {
                                                label = pw?.logNumber
                                                  ? `Piecework log #${pw.logNumber}`
                                                  : "Piecework log";
                                                if (pw) {
                                                  detail = [
                                                    pw.workCompletedDate
                                                      ? format(
                                                          new Date(
                                                            pw.workCompletedDate,
                                                          ),
                                                          "MMM d, yyyy",
                                                        )
                                                      : null,
                                                    pw.woNumber
                                                      ? `WO ${pw.woNumber}`
                                                      : null,
                                                    pw.lineItemsCount
                                                      ? `${pw.lineItemsCount} items`
                                                      : null,
                                                  ]
                                                    .filter(Boolean)
                                                    .join(" · ");
                                                }
                                              } else if (
                                                li.sourceModel === "Bill3" ||
                                                li.sourceModel ===
                                                  "SubAgreement3"
                                              ) {
                                                label =
                                                  li.sourceModel ===
                                                  "SubAgreement3"
                                                    ? "Subcontractor agreement"
                                                    : "Subcontractor bill";
                                                if (bill) {
                                                  detail = [
                                                    bill.invoiceNumber
                                                      ? `Inv #${bill.invoiceNumber}`
                                                      : null,
                                                    bill.invoiceDate
                                                      ? format(
                                                          new Date(
                                                            bill.invoiceDate,
                                                          ),
                                                          "MMM d, yyyy",
                                                        )
                                                      : null,
                                                  ]
                                                    .filter(Boolean)
                                                    .join(" · ");
                                                }
                                              } else if (
                                                li.sourceModel === "Salary"
                                              ) {
                                                label = "Salary week";
                                              } else {
                                                label =
                                                  li.sourceModel || "Line";
                                              }

                                              return (
                                                <div
                                                  key={li._id}
                                                  className="flex justify-between items-center px-4 py-2.5 text-sm"
                                                  style={{
                                                    borderTop:
                                                      i === 0
                                                        ? "none"
                                                        : "1px solid #1c2027",
                                                  }}
                                                >
                                                  <span className="text-gray-300 text-xs">
                                                    {label}
                                                    {detail && (
                                                      <span className="block text-[11px] text-gray-500 mt-0.5">
                                                        {detail}
                                                      </span>
                                                    )}
                                                  </span>
                                                  <div className="flex items-center gap-3">
                                                    <span className="font-mono text-gray-200">
                                                      {money(li.totalPay)}
                                                    </span>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeLine(li._id);
                                                      }}
                                                      className="text-red-500 hover:text-red-400 text-xs"
                                                    >
                                                      Remove
                                                    </button>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      );
                                    })()}

                                  {isLoading ? (
                                    <div className="text-center py-8 text-gray-500 text-sm">
                                      Loading breakdown…
                                    </div>
                                  ) : bd ? (
                                    <>
                                      {bd.hourly && bd.hourly.length > 0 && (
                                        <div className="mb-6">
                                          <h4 className="text-[13px] font-semibold text-gray-300 mb-3 flex items-center gap-2">
                                            Hourly timesheets
                                            <span className="text-gray-600 font-normal">
                                              ({bd.hourly.length})
                                            </span>
                                          </h4>
                                          <div
                                            className="overflow-x-auto rounded-lg"
                                            style={{
                                              border: "1px solid #1c2027",
                                            }}
                                          >
                                            <table className="min-w-full">
                                              <thead
                                                style={{
                                                  backgroundColor: "#14171d",
                                                }}
                                              >
                                                <tr>
                                                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500">
                                                    Date
                                                  </th>
                                                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500">
                                                    WO
                                                  </th>
                                                  <th className="px-4 py-2 text-right text-[10px] uppercase tracking-wider text-gray-500">
                                                    Hours
                                                  </th>
                                                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500">
                                                    Union
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {bd.hourly.map((ts, i) => (
                                                  <tr
                                                    key={i}
                                                    style={{
                                                      borderTop:
                                                        i === 0
                                                          ? "none"
                                                          : "1px solid #1c2027",
                                                    }}
                                                  >
                                                    <td className="px-4 py-2 text-sm text-gray-300 font-mono">
                                                      {ts.start
                                                        ? format(
                                                            new Date(ts.start),
                                                            "MMM d, yyyy",
                                                          )
                                                        : "—"}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-gray-300">
                                                      {ts.workOrder?.woNumber ||
                                                        "—"}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-gray-300 text-right font-mono">
                                                      {ts.hours?.toFixed(2) ||
                                                        "—"}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-gray-300">
                                                      {ts.workOrder?.union ||
                                                        "non-union"}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      )}

                                      {bd.pieceworker &&
                                        bd.pieceworker.length > 0 && (
                                          <div className="mb-6">
                                            <h4 className="text-[13px] font-semibold text-gray-300 mb-3 flex items-center gap-2">
                                              Piecework logs
                                              <span className="text-gray-600 font-normal">
                                                ({bd.pieceworker.length})
                                              </span>
                                            </h4>
                                            <div
                                              className="overflow-x-auto rounded-lg"
                                              style={{
                                                border: "1px solid #1c2027",
                                              }}
                                            >
                                              <table className="min-w-full">
                                                <thead
                                                  style={{
                                                    backgroundColor: "#14171d",
                                                  }}
                                                >
                                                  <tr>
                                                    <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500">
                                                      Log #
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500">
                                                      Work Completed
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500">
                                                      WO
                                                    </th>
                                                    <th className="px-4 py-2 text-right text-[10px] uppercase tracking-wider text-gray-500">
                                                      Subtotal
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500">
                                                      Items
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500">
                                                      Union
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {bd.pieceworker.map(
                                                    (log, i) => (
                                                      <tr
                                                        key={i}
                                                        style={{
                                                          borderTop:
                                                            i === 0
                                                              ? "none"
                                                              : "1px solid #1c2027",
                                                        }}
                                                      >
                                                        <td className="px-4 py-2 text-sm text-gray-300 font-mono">
                                                          {log.logNumber || "—"}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-300 font-mono">
                                                          {log.workCompletedDate
                                                            ? format(
                                                                new Date(
                                                                  log.workCompletedDate,
                                                                ),
                                                                "MMM d, yyyy",
                                                              )
                                                            : "—"}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-300">
                                                          {log.woNumber || "—"}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-300 text-right font-mono">
                                                          {money(log.subtotal)}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-300">
                                                          {log.lineItemsCount ||
                                                            0}{" "}
                                                          items
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-300">
                                                          {log.union ||
                                                            "non-union"}
                                                        </td>
                                                      </tr>
                                                    ),
                                                  )}
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        )}

                                      {bd.salary && (
                                        <div className="mb-6">
                                          <h4 className="text-[13px] font-semibold text-gray-300 mb-3">
                                            Salary
                                          </h4>
                                          <div
                                            className="rounded-lg p-4"
                                            style={{
                                              border: "1px solid #1c2027",
                                              backgroundColor: "#14171d",
                                            }}
                                          >
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                              <div>
                                                <p className="text-[10px] uppercase tracking-wider text-gray-500">
                                                  Hours
                                                </p>
                                                <p className="text-sm font-medium text-white font-mono">
                                                  {bd.salary.hoursPerWeek}
                                                </p>
                                              </div>
                                              <div>
                                                <p className="text-[10px] uppercase tracking-wider text-gray-500">
                                                  Pay Rate
                                                </p>
                                                <p className="text-sm font-medium text-white font-mono">
                                                  {money(bd.salary.payRate)}
                                                </p>
                                              </div>
                                              <div>
                                                <p className="text-[10px] uppercase tracking-wider text-gray-500">
                                                  Amount
                                                </p>
                                                <p className="text-sm font-medium text-white font-mono">
                                                  {money(bd.salary.amount)}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )}

                                      {bd.subcontractor &&
                                        bd.subcontractor.length > 0 && (
                                          <div className="mb-6">
                                            <h4 className="text-[13px] font-semibold text-gray-300 mb-3">
                                              Subcontractor bills
                                              <span className="text-gray-600 font-normal ml-2">
                                                ({bd.subcontractor.length})
                                              </span>
                                            </h4>
                                            <div
                                              className="overflow-x-auto rounded-lg"
                                              style={{
                                                border: "1px solid #1c2027",
                                              }}
                                            >
                                              <table className="min-w-full">
                                                <thead
                                                  style={{
                                                    backgroundColor: "#14171d",
                                                  }}
                                                >
                                                  <tr>
                                                    <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500">
                                                      Invoice #
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500">
                                                      Paid
                                                    </th>
                                                    <th className="px-4 py-2 text-right text-[10px] uppercase tracking-wider text-gray-500">
                                                      Subtotal
                                                    </th>
                                                    <th className="px-4 py-2 text-right text-[10px] uppercase tracking-wider text-gray-500">
                                                      Tax
                                                    </th>
                                                    <th className="px-4 py-2 text-right text-[10px] uppercase tracking-wider text-gray-500">
                                                      Total
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {bd.subcontractor.map(
                                                    (bill, i) => (
                                                      <tr
                                                        key={i}
                                                        style={{
                                                          borderTop:
                                                            i === 0
                                                              ? "none"
                                                              : "1px solid #1c2027",
                                                        }}
                                                      >
                                                        <td className="px-4 py-2 text-sm text-gray-300 font-mono">
                                                          {bill.invoiceNumber}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-300 font-mono">
                                                          {bill.datePaid
                                                            ? format(
                                                                new Date(
                                                                  bill.datePaid,
                                                                ),
                                                                "MMM d, yyyy",
                                                              )
                                                            : "—"}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-300 text-right font-mono">
                                                          {money(bill.subtotal)}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-300 text-right font-mono">
                                                          {money(bill.tax)}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-300 text-right font-mono">
                                                          {money(bill.total)}
                                                        </td>
                                                      </tr>
                                                    ),
                                                  )}
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        )}

                                      <div
                                        className="p-5 rounded-lg"
                                        style={{
                                          backgroundColor: "#0c0e12",
                                          border: "1px solid #1c2027",
                                        }}
                                      >
                                        <h4 className="text-[13px] font-semibold text-gray-300 mb-4">
                                          Summary
                                        </h4>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                                          <div>
                                            <p className="text-[10px] uppercase tracking-wider text-gray-500">
                                              Total Hours
                                            </p>
                                            <p className="text-xl font-bold text-white mt-1 font-mono">
                                              {(bd.totalHours || 0).toFixed(2)}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-[10px] uppercase tracking-wider text-gray-500">
                                              Subtotal
                                            </p>
                                            <p className="text-xl font-bold text-white mt-1 font-mono">
                                              {money(bd.amount)}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-[10px] uppercase tracking-wider text-gray-500">
                                              Tax
                                            </p>
                                            <p className="text-xl font-bold text-white mt-1 font-mono">
                                              {money(bd.tax)}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-[10px] uppercase tracking-wider text-gray-500">
                                              Total Pay
                                            </p>
                                            <p
                                              className="text-xl font-bold mt-1 font-mono"
                                              style={{ color: ACCENT }}
                                            >
                                              {money(bd.totalPay)}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-center py-8 text-gray-500 text-sm">
                                      No breakdown data available
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan="11"
                        className="px-5 py-6 text-center text-sm text-gray-500"
                      >
                        No payroll records available for this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {openReportFilterColumn &&
              reportFilterDropdownPos &&
              (() => {
                const col = REPORT_COLUMNS.find(
                  (c) => c.key === openReportFilterColumn,
                );
                if (!col) return null;
                const options = reportColumnOptions(col.key);
                const current = reportColumnFilters[col.key];
                const selectedSet =
                  current instanceof Set ? current : new Set();

                return (
                  <div
                    data-report-filter-dropdown
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "fixed",
                      top: reportFilterDropdownPos.top,
                      left: reportFilterDropdownPos.left,
                      zIndex: 200,
                    }}
                    className="bg-[#161a20] border border-[#2a2f37] rounded-lg shadow-2xl w-64 max-h-80 overflow-hidden flex flex-col"
                  >
                    <div className="flex items-center justify-between px-3 py-2 border-b border-[#22262e]">
                      <span className="text-[10px] uppercase tracking-wider text-gray-500">
                        {col.label}
                      </span>
                      <div className="flex items-center gap-3">
                        {selectedSet.size > 0 && (
                          <span className="text-[10px] text-orange-400">
                            {selectedSet.size} selected
                          </span>
                        )}
                        {isReportColumnFilterActive(col.key) && (
                          <button
                            onClick={() => clearReportColumnFilter(col.key)}
                            className="text-[11px] text-orange-400 hover:text-orange-300"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                    {options.length > 8 && (
                      <div className="px-2 pt-2">
                        <input
                          autoFocus
                          type="text"
                          placeholder={`Search ${col.label.toLowerCase()}…`}
                          value={
                            reportColumnFilters[`${col.key}__search`] || ""
                          }
                          onChange={(e) =>
                            setReportColumnFilters((prev) => ({
                              ...prev,
                              [`${col.key}__search`]: e.target.value,
                            }))
                          }
                          className="w-full rounded bg-[#0e1014] border border-[#2a2f37] text-gray-200 text-xs py-1.5 px-2 mb-1 focus:outline-none focus:border-orange-500"
                        />
                      </div>
                    )}
                    <div className="overflow-y-auto py-1 flex-1">
                      {options
                        .filter((val) => {
                          const q = (
                            reportColumnFilters[`${col.key}__search`] || ""
                          ).toLowerCase();
                          return !q || val.toLowerCase().includes(q);
                        })
                        .map((val) => (
                          <label
                            key={val}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-300 hover:bg-[#1c2027] cursor-pointer normal-case font-normal"
                          >
                            <input
                              type="checkbox"
                              checked={selectedSet.has(val)}
                              onChange={() =>
                                toggleReportChoiceFilterValue(col.key, val)
                              }
                              className="h-3.5 w-3.5 accent-orange-500 rounded bg-[#0e1014]"
                            />
                            <span className="truncate">{val}</span>
                          </label>
                        ))}
                      {options.length === 0 && (
                        <div className="px-3 py-4 text-xs text-gray-600 text-center">
                          No values
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
          </>
        )}
      </div>
    </div>
  );
}
